import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import PlayerScoutCard from "../components/PlayerScoutCard";
import ScoutDetailModal from "../components/ScoutDetailModal";
import ChartImpact from "../components/ChartImpact";
import { API_BASE_URL } from "../config/api";

const LAST_SCOUT_STORAGE_KEY = "myimpactgame:last-scout";
const GUESS_WHO_WON_URL = "https://guess-who-won.vercel.app/";

// --- CONSTANTES ---
const CATEGORIZED_STATS = {
  "Dégâts": ["damageDealtToBuildings", "damageDealtToObjectives", "damageDealtToTurrets", "totalDamageDealt", "totalDamageDealtToChampions", "physicalDamageDealtToChampions", "magicDamageDealtToChampions", "trueDamageDealtToChampions"],
  "Tankiness": ["totalDamageTaken", "damageSelfMitigated"],
  "Soins / Boucliers": ["effectiveHealAndShielding", "totalDamageShieldedOnTeammates", "totalHealsOnTeammates", "totalHeal"],
  "Contrôle": ["enemyChampionImmobilizations", "timeCCingOthers", "totalTimeCCDealt"],
  "Kills / Participation": ["deaths", "soloKills", "killParticipation", "pickKillWithAlly", "immobilizeAndKillWithAlly", "killAfterHiddenWithAlly"],
  "Économie / CS": ["goldEarned", "totalMinionsKilled", "totalAllyJungleMinionsKilled", "totalEnemyJungleMinionsKilled", "turretKills"],
  "Early impact": ["earlyTakedowns", "earlyCs10", "earlyCsLead", "earlyLevelLead", "earlyPlatesTaken", "killsOnLanersEarlyJungleAsJungler", "killsOnOtherLanesEarlyJungleAsLaner"],
  "Vision": ["controlWardsPlaced", "wardsGuarded", "wardsKilled", "wardsPlaced"],
};

const ROLES_ORDER = ["TOP", "JGL", "MID", "ADC", "SUP"];

// --- GÉNÉRATION DU MOCK ---
const generateDetailedGames = (mainChamp, altChamp1, altChamp2) => {
  const games = [];
  const createGame = (champion, winProbability) => {
    const isWin = Math.random() > (1 - winProbability);
    return {
      champion, win: isWin,
      kills: Math.floor(Math.random() * (isWin ? 12 : 5)),
      deaths: Math.floor(Math.random() * (isWin ? 4 : 10)),
      assists: Math.floor(Math.random() * 15),
      cs_total: 120 + Math.floor(Math.random() * 100),
    };
  };

  for (let i = 0; i < 12; i++) games.push(createGame(mainChamp, 0.6));
  for (let i = 0; i < 6; i++) games.push(createGame(altChamp1, 0.5));
  for (let i = 0; i < 2; i++) games.push(createGame(altChamp2, 0.4));
  return games.sort(() => Math.random() - 0.5);
};

const generateMockImpact = (roleStr, variance = 0) => {
  const impact = {
    totalDamageDealtToChampions: (Math.random() * 40 - 20) + variance,
    physicalDamageDealtToChampions: (Math.random() * 40 - 20) + variance,
    magicDamageDealtToChampions: (Math.random() * 40 - 20) + variance,
    trueDamageDealtToChampions: (Math.random() * 20 - 10) + variance,
    totalDamageDealt: (Math.random() * 50 - 25) + variance,
    damageDealtToObjectives: (Math.random() * 30 - 15) + variance,
    damageDealtToBuildings: (Math.random() * 20 - 10) + variance,
    damageDealtToTurrets: (Math.random() * 20 - 10) + variance,
    killParticipation: (Math.random() * 25 - 5) + variance,
    soloKills: (Math.random() * 20 - 10) + variance,
    deaths: -(Math.random() * 15) + variance,
    pickKillWithAlly: (Math.random() * 15 - 5) + variance,
    wardsPlaced: (Math.random() * 50 - 25) + variance,
    controlWardsPlaced: (Math.random() * 20 - 10) + variance,
    wardsKilled: (Math.random() * 15 - 5) + variance,
    totalDamageTaken: (Math.random() * 40 - 20) + variance,
    damageSelfMitigated: (Math.random() * 30 - 15) + variance,
    totalHealsOnTeammates: (Math.random() * 30 - 15) + variance,
    effectiveHealAndShielding: (Math.random() * 20 - 10) + variance,
    enemyChampionImmobilizations: (Math.random() * 20 - 10) + variance,
    timeCCingOthers: (Math.random() * 25 - 10) + variance,
    goldEarned: (Math.random() * 15 - 5) + variance,
    totalMinionsKilled: (Math.random() * 20 - 10) + variance,
    earlyTakedowns: (Math.random() * 24 - 12) + variance,
    earlyCs10: (Math.random() * 26 - 13) + variance,
    earlyCsLead: (Math.random() * 20 - 10) + variance,
    earlyLevelLead: (Math.random() * 14 - 7) + variance,
    earlyPlatesTaken: (Math.random() * 18 - 9) + variance,
    killsOnLanersEarlyJungleAsJungler: (Math.random() * 16 - 8) + variance,
    killsOnOtherLanesEarlyJungleAsLaner: (Math.random() * 16 - 8) + variance,
  };

  if (roleStr.includes("Sup")) { impact.wardsPlaced += 40; impact.totalHealsOnTeammates += 50; }
  if (roleStr.includes("Adc") || roleStr.includes("Mid")) { impact.totalDamageDealtToChampions += 35; }
  if (roleStr.includes("Top") || roleStr.includes("Jgl")) { impact.totalDamageTaken += 35; }

  return impact;
};

// Pool de champions pour le mock maîtrises (noms Data Dragon)
const MOCK_CHAMP_POOL = [
  "Aatrox","Ahri","Akali","Ashe","Azir","Bard","Brand","Braum","Caitlyn","Camille",
  "Cassiopeia","Darius","Diana","Draven","Ekko","Elise","Ezreal","Fiora","Fizz","Garen",
  "Gnar","Gragas","Hecarim","Irelia","Janna","Jax","Jayce","Jhin","Jinx","Karma",
  "Kassadin","Kayle","Kennen","Leona","Lissandra","Lucian","Lulu","Malphite","Morgana",
  "Nami","Nautilus","Nidalee","Ornn","Orianna","Pantheon","Riven","Sejuani","Sivir",
  "Sion","Soraka","Swain","Syndra","Talon","Thresh","Tristana","Viego","Vi","Viktor",
  "Volibear","Warwick","XinZhao","Xayah","Yasuo","Yone","Yuumi","Zac","Zed","Zyra",
];

const generateMockMasteries = (main, alts) => {
  const now = Date.now();
  const sixMonths = 6 * 30 * 24 * 60 * 60 * 1000;
  const featured = [main, ...alts];
  const rest = MOCK_CHAMP_POOL.filter(c => !featured.includes(c))
    .sort(() => Math.random() - 0.5);
  const count = 12 + Math.floor(Math.random() * 7); // 12-18
  const all = [...featured, ...rest].slice(0, count);

  return all
    .map((champ, i) => ({
      champion:     champ,
      level:        Math.max(1, Math.min(7, 7 - Math.floor(i * 6 / count))),
      points:       Math.max(5000, Math.floor(750000 * Math.pow(0.65, i) + Math.random() * 20000)),
      lastPlayTime: now - Math.floor(Math.random() * sixMonths),
    }))
    .sort((a, b) => b.points - a.points);
};

const createMockPlayer = (name, tag, rank, main, alt1, alt2) => {
  const gamesList = generateDetailedGames(main, alt1, alt2);
  const seasonWins = 42 + Math.floor(Math.random() * 80);
  const seasonLosses = 35 + Math.floor(Math.random() * 75);
  const champsMap = {};
  gamesList.forEach(g => {
    if (!champsMap[g.champion]) {
      champsMap[g.champion] = {
        champion: g.champion, games: 0, wins: 0,
        impact: generateMockImpact(name, Math.random() * 20 - 10)
      };
    }
    champsMap[g.champion].games++;
    if (g.win) champsMap[g.champion].wins++;
  });

  const championsArray = Object.values(champsMap).map(c => ({
    ...c, winrate: Math.round((c.wins / c.games) * 100)
  })).sort((a, b) => b.games - a.games);

  return {
    gameName: name, tagLine: tag, gamesCount: 20, rank: rank,
    seasonWins,
    seasonLosses,
    seasonWinrate: Math.round((seasonWins / (seasonWins + seasonLosses)) * 100),
    masteries: generateMockMasteries(main, [alt1, alt2]),
    games: gamesList, impact: generateMockImpact(name), champions: championsArray
  };
};

const mockTeam1 = [
  createMockPlayer("ImpactTop", "EUW", "Diamond 2", "Renekton", "Jax", "Sion"),
  createMockPlayer("ImpactJgl", "EUW", "Master", "LeeSin", "Sejuani", "Vi"),
  createMockPlayer("ImpactMid", "EUW", "Diamond 1", "Ahri", "Syndra", "Orianna"),
  createMockPlayer("ImpactAdc", "EUW", "Grandmaster", "Jinx", "Kaisa", "Xayah"),
  createMockPlayer("ImpactSup", "EUW", "Diamond 3", "Thresh", "Nautilus", "Lulu"),
];

const mockTeam2 = [
  createMockPlayer("EnemyTop", "FR", "Emerald 1", "Ornn", "Garen", "Darius"),
  createMockPlayer("EnemyJgl", "FR", "Diamond 4", "XinZhao", "Zac", "Viego"),
  createMockPlayer("EnemyMid", "FR", "Diamond 2", "Zed", "Talon", "Akali"),
  createMockPlayer("EnemyAdc", "FR", "Diamond 3", "Lucian", "Ezreal", "Ashe"),
  createMockPlayer("EnemySup", "FR", "Emerald 2", "Nami", "Karma", "Leona"),
];

const DRAFT_SLOT_KEYS = ["blueBans", "bluePicks", "redBans", "redPicks"];

const DRAFT_TURNS = {
  blueBans: [1, 3, 5, 14, 16],
  redBans: [2, 4, 6, 13, 15],
  bluePicks: [7, 10, 11, 18, 19],
  redPicks: [8, 9, 12, 17, 20],
};

const DRAFT_PICK_OFFSETS = {
  bluePicks: [0, 32, 32, 64, 64],
  redPicks: [32, 32, 0, 64, 0],
};

const DRAFT_SEQUENCE = [
  { turn: 1, side: "blue", action: "ban", group: "blueBans", index: 0, label: "Blue ban 1" },
  { turn: 2, side: "red", action: "ban", group: "redBans", index: 0, label: "Red ban 1" },
  { turn: 3, side: "blue", action: "ban", group: "blueBans", index: 1, label: "Blue ban 2" },
  { turn: 4, side: "red", action: "ban", group: "redBans", index: 1, label: "Red ban 2" },
  { turn: 5, side: "blue", action: "ban", group: "blueBans", index: 2, label: "Blue ban 3" },
  { turn: 6, side: "red", action: "ban", group: "redBans", index: 2, label: "Red ban 3" },
  { turn: 7, side: "blue", action: "pick", group: "bluePicks", index: 0, label: "Blue pick 1" },
  { turn: 8, side: "red", action: "pick", group: "redPicks", index: 0, label: "Red pick 1" },
  { turn: 9, side: "red", action: "pick", group: "redPicks", index: 1, label: "Red pick 2" },
  { turn: 10, side: "blue", action: "pick", group: "bluePicks", index: 1, label: "Blue pick 2" },
  { turn: 11, side: "blue", action: "pick", group: "bluePicks", index: 2, label: "Blue pick 3" },
  { turn: 12, side: "red", action: "pick", group: "redPicks", index: 2, label: "Red pick 3" },
  { turn: 13, side: "red", action: "ban", group: "redBans", index: 3, label: "Red ban 4" },
  { turn: 14, side: "blue", action: "ban", group: "blueBans", index: 3, label: "Blue ban 4" },
  { turn: 15, side: "red", action: "ban", group: "redBans", index: 4, label: "Red ban 5" },
  { turn: 16, side: "blue", action: "ban", group: "blueBans", index: 4, label: "Blue ban 5" },
  { turn: 17, side: "red", action: "pick", group: "redPicks", index: 3, label: "Red pick 4" },
  { turn: 18, side: "blue", action: "pick", group: "bluePicks", index: 3, label: "Blue pick 4" },
  { turn: 19, side: "blue", action: "pick", group: "bluePicks", index: 4, label: "Blue pick 5" },
  { turn: 20, side: "red", action: "pick", group: "redPicks", index: 4, label: "Red pick 5" },
];

const createEmptyDraftPlan = (index = 0) => ({
  name: `Draft ${index + 1}`,
  teamOneSide: "blue",
  blueBans: Array(5).fill(null),
  bluePicks: Array(5).fill(null),
  redBans: Array(5).fill(null),
  redPicks: Array(5).fill(null),
});

const DEFAULT_DRAFT_BOT_WEIGHTS = {
  recentGames: 6,
  winrate: 0.8,
  sampleSize: 10,
  sampleTargetGames: 6,
  masteryPoints: 7,
  masteryLevel: 3,
  recentBonus: 10,
  roleFit: 35,
  offRolePenalty: 30,
  compositionNeed: 1,
  counterNeed: 1,
  playerStyle: 1,
  mainChampion: 10,
  poolRead: 1,
  teamStrategy: 1,
};

const createDraftBotConfig = () => ({
  weights: { ...DEFAULT_DRAFT_BOT_WEIGHTS },
  useCompositionLogic: true,
  scoreFormula: "balanced",
  strategyMode: "adaptive",
});

const DRAFT_SCORE_FORMULAS = [
  { id: "balanced", label: "Balanced", detail: "Classic score: games, winrate, mastery and role fit." },
  { id: "reliableGames", label: "Reliable WR", detail: "Winrate is reduced on low sample size and games matter more." },
];

const DRAFT_STRATEGY_MODES = [
  { id: "adaptive", label: "Adaptive", detail: "Choose Dive In or Counter Classic from team/lane strength." },
  { id: "diveIn", label: "Dive In", detail: "Attack weak lanes, favor pressure and comfort picks." },
  { id: "counterClassic", label: "Counter Classic", detail: "Respect stronger lanes, deny mains and stabilize matchups." },
  { id: "neutral", label: "Neutral", detail: "Do not add team-strength strategy bonuses." },
];

const DRAFT_TAG_SHORT = {
  Tank: "Tk",
  Fighter: "Fi",
  Mage: "Mg",
  Assassin: "As",
  Marksman: "Mm",
  Support: "Sp",
};

const EMPTY_DRAFT_TRAITS = {
  frontline: 0,
  engage: 0,
  peel: 0,
  antiDive: 0,
  antiTank: 0,
  damageAD: 0,
  damageAP: 0,
  dive: 0,
  pick: 0,
  mobility: 0,
  control: 0,
  utility: 0,
  fragileCarry: 0,
  versatility: 0,
};

const getPlayerName = (player) => {
  const name = player?.gameName || player?.name || "Unknown";
  const tag = player?.tagLine ? `#${player.tagLine}` : "";
  return `${name}${tag}`;
};

const getChampionName = (item) => item?.champion || item?.name || item?.championName || "";

const normalizeChampionLookupKey = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const buildChampionMetaLookup = (data) => {
  const lookup = {};
  Object.values(data?.data || {}).forEach((champion) => {
    const meta = {
      id: champion.id,
      name: champion.name,
      tags: Array.isArray(champion.tags) ? champion.tags : [],
      info: champion.info || {},
    };
    [champion.id, champion.name, champion.image?.full?.replace(/\.png$/i, "")].forEach((key) => {
      const normalized = normalizeChampionLookupKey(key);
      if (normalized) lookup[normalized] = meta;
    });
  });
  return lookup;
};

const getChampionMeta = (championMeta, championName) => (
  championMeta[normalizeChampionLookupKey(championName)] || { tags: [], info: {} }
);

const addTrait = (traits, key, value) => {
  traits[key] = (traits[key] || 0) + value;
};

const scalePositiveImpact = (value, divisor = 28) => Math.max(0, Number(value || 0)) / divisor;

const getChampionDifficulty = (championMeta, championName) => {
  const difficulty = Number(getChampionMeta(championMeta || {}, championName)?.info?.difficulty);
  if (!Number.isFinite(difficulty) || difficulty <= 0) return 5;
  return Math.max(1, Math.min(10, difficulty));
};

const capMasteryPointsForChampion = (points, entry, championMeta) => {
  const difficulty = getChampionDifficulty(championMeta, entry?.name);
  return Math.min(Number(points || 0), difficulty * 200000);
};

const averageNumbers = (values) => {
  const valid = values.map(Number).filter(Number.isFinite);
  if (!valid.length) return 0;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
};

const averageImpactObjects = (impacts) => {
  const sums = {};
  const counts = {};
  impacts.filter(Boolean).forEach((impact) => {
    Object.entries(impact || {}).forEach(([key, value]) => {
      const number = Number(value);
      if (!Number.isFinite(number)) return;
      sums[key] = (sums[key] || 0) + number;
      counts[key] = (counts[key] || 0) + 1;
    });
  });
  return Object.fromEntries(Object.entries(sums).map(([key, value]) => [key, value / (counts[key] || 1)]));
};

const normalizeDraftRole = (value) => {
  const role = String(value || "").trim().toUpperCase();
  if (["TOP", "TOPLANE"].includes(role)) return "TOP";
  if (["JGL", "JG", "JUNGLE", "JUNGLER"].includes(role)) return "JGL";
  if (["MID", "MIDDLE"].includes(role)) return "MID";
  if (["ADC", "BOT", "BOTTOM"].includes(role)) return "ADC";
  if (["SUP", "SUPPORT", "UTILITY"].includes(role)) return "SUP";
  return "";
};

const getGameDraftRole = (game) => normalizeDraftRole(
  game?.role || game?.lane || game?.teamPosition || game?.individualPosition || game?.position
);

const getWinrateValue = (item, games) => {
  if (Number.isFinite(Number(item?.winrate))) return Math.round(Number(item.winrate));
  if (Number.isFinite(Number(item?.winrateValue))) return Math.round(Number(item.winrateValue));
  if (games && Number.isFinite(Number(item?.wins))) return Math.round((Number(item.wins) / games) * 100);
  return null;
};

const getChampionImpactForPlayer = (player, championName) => {
  const target = normalizeChampionLookupKey(championName);
  const championRow = (Array.isArray(player?.champions) ? player.champions : [])
    .find((champ) => normalizeChampionLookupKey(getChampionName(champ)) === target);
  return championRow?.impact || player?.impact || {};
};

const getImpactAverageForKeys = (impact, keys) => averageNumbers(keys.map((key) => impact?.[key]));

const getImpactStyleScores = (impact = {}) => ({
  early: scalePositiveImpact(getImpactAverageForKeys(impact, ["earlyTakedowns", "earlyCsLead", "earlyLevelLead", "earlyPlatesTaken", "killsOnLanersEarlyJungleAsJungler", "killsOnOtherLanesEarlyJungleAsLaner"]), 18),
  damage: scalePositiveImpact(getImpactAverageForKeys(impact, ["totalDamageDealtToChampions", "physicalDamageDealtToChampions", "magicDamageDealtToChampions", "trueDamageDealtToChampions"]), 28),
  objective: scalePositiveImpact(getImpactAverageForKeys(impact, ["damageDealtToObjectives", "damageDealtToBuildings", "damageDealtToTurrets", "turretKills"]), 22),
  peel: scalePositiveImpact(getImpactAverageForKeys(impact, ["effectiveHealAndShielding", "totalDamageShieldedOnTeammates", "totalHealsOnTeammates", "totalHeal"]), 22),
  control: scalePositiveImpact(getImpactAverageForKeys(impact, ["enemyChampionImmobilizations", "timeCCingOthers", "totalTimeCCDealt", "immobilizeAndKillWithAlly"]), 22),
  frontline: scalePositiveImpact(getImpactAverageForKeys(impact, ["totalDamageTaken", "damageSelfMitigated"]), 30),
  vision: scalePositiveImpact(getImpactAverageForKeys(impact, ["controlWardsPlaced", "wardsGuarded", "wardsKilled", "wardsPlaced"]), 26),
});

const DRAFT_STYLE_LABELS = {
  early: "early pressure",
  damage: "damage output",
  objective: "objective pressure",
  peel: "peel / utility",
  control: "crowd control",
  frontline: "frontline",
  vision: "vision control",
};

const getTopStyleLabels = (scores = {}, limit = 2) => (
  Object.entries(scores)
    .filter(([, score]) => Number(score) > 0.25)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([key]) => DRAFT_STYLE_LABELS[key] || key)
);

const getGameParticipantRole = (participant) => normalizeDraftRole(
  participant?.role || participant?.lane || participant?.teamPosition || participant?.individualPosition || participant?.position
);

const getSameLaneOpponentFromGame = (game) => {
  const role = getGameDraftRole(game);
  const participants = Array.isArray(game?.participants) ? game.participants : [];
  if (!role || !participants.length || game?.teamId === undefined || game?.teamId === null) return null;
  const exact = participants.find((participant) => (
    participant?.teamId !== game.teamId && getGameParticipantRole(participant) === role
  ));
  return exact || null;
};

const summarizeRecentMatchups = (games, fallbackImpact = {}) => (
  games.map((game) => {
    const opponent = getSameLaneOpponentFromGame(game);
    if (!opponent?.champion) return null;
    const impact = game?.impact || fallbackImpact || {};
    const styleScores = getImpactStyleScores(impact);
    return {
      champion: opponent.champion,
      role: getGameDraftRole(game),
      win: Boolean(game.win),
      impact,
      styleScores,
      impactScore: averageNumbers(Object.values(styleScores)),
    };
  }).filter(Boolean)
);

const getPlayerStylePayloadFromRefs = (refs) => {
  const recentRefs = refs.filter((ref) => ref.source === "recent");
  const impact = averageImpactObjects(recentRefs.map((ref) => ref.impact));
  const styleScores = getImpactStyleScores(impact);
  return {
    impact,
    scores: styleScores,
    labels: getTopStyleLabels(styleScores),
    strength: averageNumbers(Object.values(styleScores)),
  };
};

const buildRecentChampionRows = (player, assignedRole) => {
  const targetRole = normalizeDraftRole(assignedRole);
  const rawGames = Array.isArray(player?.games) ? player.games : [];
  const gamesWithLane = rawGames.filter((game) => getGameDraftRole(game));
  const sourceGames = gamesWithLane.length
    ? rawGames.filter((game) => getGameDraftRole(game) === targetRole)
    : rawGames;

  if (sourceGames.length) {
    const map = {};
    sourceGames.forEach((game) => {
      const champion = getChampionName(game);
      if (!champion) return;
      const fallbackImpact = getChampionImpactForPlayer(player, champion);
      if (!map[champion]) {
        map[champion] = {
          champion,
          games: 0,
          wins: 0,
          role: gamesWithLane.length ? targetRole : "",
          laneStrict: gamesWithLane.length > 0,
          impacts: [],
          matchupGames: [],
        };
      }
      map[champion].games += 1;
      if (game.win) map[champion].wins += 1;
      map[champion].impacts.push(game?.impact || fallbackImpact);
      map[champion].matchupGames.push(game);
    });

    return Object.values(map).map((champ) => ({
      ...champ,
      winrate: champ.games ? Math.round((champ.wins / champ.games) * 100) : null,
      impact: averageImpactObjects(champ.impacts),
      style: getImpactStyleScores(averageImpactObjects(champ.impacts)),
      matchups: summarizeRecentMatchups(champ.matchupGames, averageImpactObjects(champ.impacts)),
    }));
  }

  if (gamesWithLane.length) return [];

  if (Array.isArray(player?.champions) && player.champions.length) {
    return player.champions
      .map((champ) => {
        const champion = getChampionName(champ);
        const games = Number(champ.games || champ.count || 0);
        if (!champion || !games) return null;
        return {
          champion,
          games,
          wins: Number(champ.wins || 0),
          winrate: getWinrateValue(champ, games),
          impact: champ.impact || {},
          style: getImpactStyleScores(champ.impact || {}),
          matchups: [],
          role: targetRole,
          laneStrict: false,
        };
      })
      .filter(Boolean);
  }
  return [];
};

const buildDraftChampionPool = (allyPlayers, enemyPlayers) => {
  const championMap = new Map();

  const ensureChampion = (champion) => {
    const name = String(champion || "").trim();
    if (!name) return null;
    if (!championMap.has(name)) {
      championMap.set(name, {
        name,
        refs: [],
      });
    }
    return championMap.get(name);
  };

  const addRef = (champion, ref) => {
    const entry = ensureChampion(champion);
    if (!entry) return;
    entry.refs.push(ref);
  };

  const addTeam = (teamPlayers, side) => {
    teamPlayers.forEach((player, index) => {
      const role = normalizeDraftRole(player?.role) || ROLES_ORDER[index] || "ROLE";
      const playerKey = `${side}-${index}`;
      const playerName = getPlayerName(player);

      buildRecentChampionRows(player, role).forEach((champ) => {
        addRef(champ.champion, {
          source: "recent",
          side,
          role: champ.role || role,
          assignedRole: role,
          laneStrict: Boolean(champ.laneStrict),
          playerKey,
          playerName,
          games: champ.games,
          wins: champ.wins,
          winrate: champ.winrate,
          impact: champ.impact || getChampionImpactForPlayer(player, champ.champion),
          style: champ.style || getImpactStyleScores(champ.impact || getChampionImpactForPlayer(player, champ.champion)),
          matchups: champ.matchups || [],
        });
      });

      (Array.isArray(player?.masteries) ? player.masteries : []).forEach((mastery) => {
        const champion = getChampionName(mastery);
        if (!champion) return;
        addRef(champion, {
          source: "mastery",
          side,
          role,
          assignedRole: role,
          laneStrict: false,
          playerKey,
          playerName,
          masteryPoints: Number(mastery.points || mastery.championPoints || 0),
          masteryLevel: Number(mastery.level || mastery.championLevel || 0),
        });
      });
    });
  };

  addTeam(allyPlayers, "ally");
  addTeam(enemyPlayers, "enemy");

  return Array.from(championMap.values())
    .map((entry) => {
      const recentRefs = entry.refs.filter((ref) => ref.source === "recent");
      const masteryRefs = entry.refs.filter((ref) => ref.source === "mastery");
      const allyRefs = entry.refs.filter((ref) => ref.side === "ally");
      const enemyRefs = entry.refs.filter((ref) => ref.side === "enemy");
      const allyRecentRefs = recentRefs.filter((ref) => ref.side === "ally");
      const enemyRecentRefs = recentRefs.filter((ref) => ref.side === "enemy");
      const allyRecentRoles = new Set(allyRecentRefs.map((ref) => ref.role).filter(Boolean));
      const enemyRecentRoles = new Set(enemyRecentRefs.map((ref) => ref.role).filter(Boolean));
      const totalRecentGames = recentRefs.reduce((sum, ref) => sum + Number(ref.games || 0), 0);
      const weighted = recentRefs.reduce((acc, ref) => {
        const games = Number(ref.games || 0);
        if (!games || ref.winrate === null || ref.winrate === undefined) return acc;
        return { games: acc.games + games, total: acc.total + (Number(ref.winrate) * games) };
      }, { games: 0, total: 0 });
      const bestMasteryPoints = masteryRefs.reduce((max, ref) => Math.max(max, Number(ref.masteryPoints || 0)), 0);
      const bestMasteryLevel = masteryRefs.reduce((max, ref) => Math.max(max, Number(ref.masteryLevel || 0)), 0);

      return {
        ...entry,
        recentRefs,
        masteryRefs,
        hasAlly: allyRefs.length > 0,
        hasEnemy: enemyRefs.length > 0,
        hasRecentAlly: allyRecentRefs.length > 0,
        hasRecentEnemy: enemyRecentRefs.length > 0,
        isRecent: recentRefs.length > 0,
        isMastery: masteryRefs.length > 0,
        isContested: allyRecentRefs.length > 0 && enemyRecentRefs.length > 0,
        isAllyFlexRecent: allyRecentRoles.size > 1,
        isEnemyFlexRecent: enemyRecentRoles.size > 1,
        isFlex: allyRecentRoles.size > 1 || enemyRecentRoles.size > 1,
        roles: Array.from(new Set(entry.refs.map((ref) => ref.role).filter(Boolean))),
        playerKeys: Array.from(new Set(entry.refs.map((ref) => ref.playerKey))),
        playerNames: Array.from(new Set(entry.refs.map((ref) => ref.playerName))),
        totalRecentGames,
        winrate: weighted.games ? Math.round(weighted.total / weighted.games) : null,
        bestMasteryPoints,
        bestMasteryLevel,
      };
    })
    .sort((a, b) => {
      if (b.totalRecentGames !== a.totalRecentGames) return b.totalRecentGames - a.totalRecentGames;
      if (b.bestMasteryPoints !== a.bestMasteryPoints) return b.bestMasteryPoints - a.bestMasteryPoints;
      return a.name.localeCompare(b.name);
    });
};

const getDraftSignal = (entry) => {
  if (!entry) return "neutral";
  if (entry.isContested) return "contested";
  if (entry.isAllyFlexRecent) return "allyFlex";
  if (entry.isEnemyFlexRecent) return "enemyFlex";
  if (entry.hasRecentAlly) return "ally";
  if (entry.hasRecentEnemy) return "enemy";
  return "neutral";
};

const getDraftSignalStyle = (signal, C) => {
  const styles = {
    ally: { label: "Team One recent", color: C.team1, border: "rgba(74,222,128,0.58)", bg: "rgba(74,222,128,0.09)" },
    enemy: { label: "Team Two recent", color: C.team2, border: "rgba(248,113,113,0.58)", bg: "rgba(248,113,113,0.09)" },
    contested: { label: "Recent contested", color: C.diff, border: "rgba(96,165,250,0.62)", bg: "rgba(59,130,246,0.11)" },
    allyFlex: { label: "Team One flex", color: "#fbbf24", border: "rgba(251,191,36,0.62)", bg: "rgba(251,191,36,0.11)" },
    enemyFlex: { label: "Team Two flex", color: "#c084fc", border: "rgba(192,132,252,0.62)", bg: "rgba(168,85,247,0.10)" },
    neutral: { label: "Mastery / known", color: "#a1a1aa", border: "rgba(161,161,170,0.24)", bg: "rgba(161,161,170,0.045)" },
  };
  return styles[signal] || styles.neutral;
};

const getEntryRefs = (entry, dataSide, role = null, options = {}) => {
  const normalizedRole = normalizeDraftRole(role);
  return (entry?.refs || []).filter((ref) => {
    if (ref.side !== dataSide) return false;
    if (normalizedRole && normalizeDraftRole(ref.role) !== normalizedRole) return false;
    if (options.source && ref.source !== options.source) return false;
    if (options.strictLane && !ref.laneStrict) return false;
    return true;
  });
};

const hasStrictLaneRecent = (entry, dataSide, role) => (
  getEntryRefs(entry, dataSide, role, { source: "recent", strictLane: true }).length > 0
);

const hasAnyLaneKnowledge = (entry, dataSide) => (
  getEntryRefs(entry, dataSide, null, { source: "recent", strictLane: true }).length > 0
);

const getDraftRoleCandidates = (entries, dataSide, role) => {
  const strict = entries.filter((entry) => hasStrictLaneRecent(entry, dataSide, role));
  if (strict.length) return strict;

  const sideHasLaneData = entries.some((entry) => hasAnyLaneKnowledge(entry, dataSide));
  if (sideHasLaneData) return [];

  return entries.filter((entry) => getEntryRefs(entry, dataSide, role).length);
};

const getRecentRolesForEntry = (entry, dataSide) => (
  Array.from(new Set(
    getEntryRefs(entry, dataSide, null, { source: "recent" })
      .map((ref) => normalizeDraftRole(ref.role))
      .filter(Boolean)
  ))
);

const getAvailableBanRolesForEntry = (entry, dataSide, openRoles) => {
  const recentRoles = getRecentRolesForEntry(entry, dataSide);
  const allowedRoles = openRoles.length ? openRoles : ROLES_ORDER;

  if (recentRoles.length) {
    return recentRoles.filter((role) => allowedRoles.includes(role));
  }

  return allowedRoles.filter((role) => getEntryRefs(entry, dataSide, role).length);
};

const getEntryImpactAverage = (entry, dataSide, role, keys) => {
  const roleRefs = role ? getEntryRefs(entry, dataSide, role, { source: "recent" }) : [];
  const refs = roleRefs.length ? roleRefs : getEntryRefs(entry, dataSide, null, { source: "recent" });
  const values = refs.flatMap((ref) => keys.map((key) => Number(ref.impact?.[key])).filter(Number.isFinite));
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const getEntryImpactSignals = (entry, dataSide, role) => ({
  damage: getEntryImpactAverage(entry, dataSide, role, ["totalDamageDealtToChampions", "physicalDamageDealtToChampions", "magicDamageDealtToChampions", "trueDamageDealtToChampions"]),
  physicalDamage: getEntryImpactAverage(entry, dataSide, role, ["physicalDamageDealtToChampions"]),
  magicDamage: getEntryImpactAverage(entry, dataSide, role, ["magicDamageDealtToChampions"]),
  peel: getEntryImpactAverage(entry, dataSide, role, ["effectiveHealAndShielding", "totalDamageShieldedOnTeammates", "totalHealsOnTeammates"]),
  control: getEntryImpactAverage(entry, dataSide, role, ["enemyChampionImmobilizations", "timeCCingOthers", "totalTimeCCDealt"]),
  tankiness: getEntryImpactAverage(entry, dataSide, role, ["totalDamageTaken", "damageSelfMitigated"]),
  early: getEntryImpactAverage(entry, dataSide, role, ["earlyTakedowns", "killsOnLanersEarlyJungleAsJungler", "killsOnOtherLanesEarlyJungleAsLaner"]),
});

const getEntryStylePayload = (entry, dataSide, role) => {
  const roleRefs = role ? getEntryRefs(entry, dataSide, role, { source: "recent" }) : [];
  const refs = roleRefs.length ? roleRefs : getEntryRefs(entry, dataSide, null, { source: "recent" });
  return getPlayerStylePayloadFromRefs(refs);
};

const getStyleTraitFitScore = (styleScores, traits) => {
  if (!styleScores || !traits) return 0;
  return (
    Number(styleScores.early || 0) * (Number(traits.pick || 0) + Number(traits.dive || 0) + Number(traits.mobility || 0) * 0.8)
    + Number(styleScores.damage || 0) * (Number(traits.damageAD || 0) + Number(traits.damageAP || 0) + Number(traits.antiTank || 0) * 0.75)
    + Number(styleScores.objective || 0) * (Number(traits.damageAD || 0) * 0.45 + Number(traits.antiTank || 0) * 0.45 + Number(traits.versatility || 0) * 0.35)
    + Number(styleScores.peel || 0) * (Number(traits.peel || 0) + Number(traits.utility || 0) + Number(traits.antiDive || 0) * 0.75)
    + Number(styleScores.control || 0) * (Number(traits.control || 0) + Number(traits.pick || 0) * 0.35 + Number(traits.engage || 0) * 0.35)
    + Number(styleScores.frontline || 0) * (Number(traits.frontline || 0) + Number(traits.antiDive || 0) * 0.45)
    + Number(styleScores.vision || 0) * (Number(traits.utility || 0) * 0.55 + Number(traits.control || 0) * 0.35 + Number(traits.peel || 0) * 0.35)
  );
};

const getPlayerStyleFitPayload = (entry, dataSide, role, candidateTraits, weights) => {
  const style = getEntryStylePayload(entry, dataSide, role);
  if (!style.labels.length || style.strength < 0.18) return { score: 0, reasons: [], style };
  const rawScore = getStyleTraitFitScore(style.scores, candidateTraits);
  const score = Math.min(16, rawScore * Number(weights?.playerStyle || 0));
  const reasons = score > 1.5 ? [`matches this player's recent ${style.labels.join(" / ")} style`] : [];
  return { score, reasons, style };
};

const getDraftTraitsForChampion = (entry, dataSide, role, championMeta) => {
  const meta = getChampionMeta(championMeta, entry?.name);
  const tags = meta.tags || [];
  const primary = tags[0] || "";
  const secondary = tags[1] || "";
  const info = meta.info || {};
  const traits = { ...EMPTY_DRAFT_TRAITS };
  const isRole = (target) => normalizeDraftRole(role) === target;
  const singleTag = tags.length === 1;
  if (tags.length > 1) addTrait(traits, "versatility", Math.min(1.4, 0.65 + (tags.length - 2) * 0.25));

  tags.forEach((tag, index) => {
    const primaryScale = index === 0 ? 1 : 0.72;
    if (tag === "Tank") {
      addTrait(traits, "frontline", 2.2 * primaryScale);
      addTrait(traits, "engage", 0.55 * primaryScale);
      addTrait(traits, "antiDive", 0.8 * primaryScale);
      if (isRole("SUP")) addTrait(traits, "peel", 0.8);
    }
    if (tag === "Fighter") {
      addTrait(traits, "frontline", 0.9 * primaryScale);
      addTrait(traits, "antiTank", (isRole("TOP") || isRole("JGL") ? 1.45 : 0.85) * primaryScale);
      addTrait(traits, "dive", (isRole("TOP") || isRole("JGL") ? 0.75 : 0.45) * primaryScale);
      addTrait(traits, "engage", 0.25 * primaryScale);
      addTrait(traits, "damageAD", 0.8 * primaryScale);
    }
    if (tag === "Assassin") {
      addTrait(traits, "dive", (isRole("MID") || isRole("JGL") ? 1.95 : 1.25) * primaryScale);
      addTrait(traits, "mobility", 1.5 * primaryScale);
      addTrait(traits, "pick", 1.25 * primaryScale);
      addTrait(traits, "engage", 0.2 * primaryScale);
      addTrait(traits, "fragileCarry", 0.35 * primaryScale);
    }
    if (tag === "Mage") {
      addTrait(traits, "damageAP", 1.75 * primaryScale);
      addTrait(traits, "control", 0.75 * primaryScale);
      addTrait(traits, "pick", (isRole("MID") ? 0.75 : 0.35) * primaryScale);
      if (!isRole("SUP")) addTrait(traits, "fragileCarry", isRole("MID") ? 0.8 * primaryScale : 0.45 * primaryScale);
      if (singleTag && !isRole("SUP")) addTrait(traits, "antiTank", 0.65);
      if (isRole("SUP")) addTrait(traits, "utility", 0.75);
    }
    if (tag === "Marksman") {
      addTrait(traits, "damageAD", 2.25 * primaryScale);
      addTrait(traits, "antiTank", 1.6 * primaryScale);
      addTrait(traits, "fragileCarry", 1.45 * primaryScale);
      if (isRole("ADC")) addTrait(traits, "fragileCarry", 0.55);
    }
    if (tag === "Support") {
      addTrait(traits, "utility", 1.65 * primaryScale);
      addTrait(traits, "peel", 1.15 * primaryScale);
      addTrait(traits, "control", 0.55 * primaryScale);
      if (isRole("SUP")) addTrait(traits, "antiDive", 0.6);
    }
  });

  if (primary === "Tank" && secondary === "Mage") addTrait(traits, "engage", 0.65);
  if (primary === "Mage" && secondary === "Assassin") addTrait(traits, "pick", 0.65);
  if (primary === "Fighter" && secondary === "Assassin") addTrait(traits, "dive", 0.75);
  if (primary === "Tank" && secondary === "Support") addTrait(traits, "peel", 0.7);
  if (primary === "Support" && secondary === "Mage") addTrait(traits, "peel", 0.45);

  if (Number(info.attack) >= 7) addTrait(traits, "damageAD", 0.45);
  if (Number(info.defense) >= 7) addTrait(traits, "frontline", 0.55);
  if (Number(info.magic) >= 7) addTrait(traits, "damageAP", 0.45);

  const impact = getEntryImpactSignals(entry, dataSide, role);
  addTrait(traits, "damageAD", scalePositiveImpact(impact.physicalDamage, 32));
  addTrait(traits, "damageAP", scalePositiveImpact(impact.magicDamage, 32));
  addTrait(traits, "antiTank", scalePositiveImpact(impact.damage, 36));
  addTrait(traits, "peel", scalePositiveImpact(impact.peel, 24));
  addTrait(traits, "control", scalePositiveImpact(impact.control, 24));
  addTrait(traits, "antiDive", scalePositiveImpact(impact.control, 42));
  addTrait(traits, "frontline", scalePositiveImpact(impact.tankiness, 34));
  addTrait(traits, "pick", scalePositiveImpact(impact.early, 44));
  addTrait(traits, "mobility", scalePositiveImpact(impact.early, 58));

  return traits;
};

const sumDraftTraits = (profiles) => profiles.reduce((acc, profile) => {
  Object.keys(EMPTY_DRAFT_TRAITS).forEach((key) => {
    acc[key] = (acc[key] || 0) + Number(profile?.traits?.[key] || 0);
  });
  return acc;
}, { ...EMPTY_DRAFT_TRAITS });

const getTeamDraftNeeds = (ownTraits, enemyTraits, context = {}) => {
  const needs = [];
  const addNeed = (id, label, weight, matches) => needs.push({ id, label, weight, matches });
  const candidateRole = normalizeDraftRole(context.candidateRole);
  const ownRoleSet = context.ownRoles instanceof Set ? context.ownRoles : new Set(context.ownRoles || []);
  const enemyRoleTraits = context.enemyRoleProfile?.traits || null;
  const roleIsOpen = candidateRole && !ownRoleSet.has(candidateRole);
  const enemyDivePressure = enemyTraits.dive + Math.max(0, enemyTraits.mobility - 1) * 0.75 + enemyTraits.engage * 0.25;
  const enemyEngagePressure = enemyTraits.engage + enemyTraits.pick * 0.6 + enemyTraits.control * 0.35;
  const enemySlowFrontline = enemyTraits.frontline + enemyTraits.peel * 0.7 + enemyTraits.control * 0.45;

  if (ownTraits.frontline < 1.25 || (ownTraits.fragileCarry > 1.1 && ownTraits.frontline < 1.85)) {
    addNeed("frontline", "needs one frontline anchor", 10, { frontline: 1, antiDive: 0.25, versatility: 0.25 });
  }
  if (ownTraits.engage < 1.1 && ownTraits.pick < 1.35) {
    addNeed("engage", "needs a way to start or find picks", 9, { engage: 0.75, pick: 1, dive: 0.35, mobility: 0.25, versatility: 0.25 });
  }
  if (ownTraits.damageAD < 1.6) addNeed("adDamage", "needs AD damage", 9, { damageAD: 1, antiTank: 0.45 });
  if (ownTraits.damageAP < 1.35) addNeed("apDamage", "needs AP damage", 9, { damageAP: 1, control: 0.25 });
  if (ownTraits.fragileCarry > 0.9 && ownTraits.peel < 1.4) addNeed("protectCarry", "needs carry protection", 15, { peel: 1, antiDive: 0.75, control: 0.4, frontline: 0.25 });
  if (enemyDivePressure > 3.65) addNeed("antiDive", "needs anti-dive or peel", 13, { peel: 0.95, antiDive: 1, control: 0.55, frontline: 0.2 });
  if (enemyEngagePressure > 4.1 && (ownTraits.peel + ownTraits.control) < 2.8) addNeed("disengage", "needs disengage or control", 10, { peel: 0.75, antiDive: 0.65, control: 1, versatility: 0.25 });
  if (enemySlowFrontline > 4.8) addNeed("antiTank", "needs damage into enemy frontline", 12, { antiTank: 1, damageAD: 0.5, damageAP: 0.35, versatility: 0.2 });
  if (enemyTraits.fragileCarry > 1.2 && ownTraits.pick < 1.7) addNeed("backlineThreat", "can threaten enemy carries", 9, { dive: 0.8, pick: 1, mobility: 0.55, engage: 0.25, versatility: 0.25 });

  if (roleIsOpen && enemyRoleTraits) {
    if (candidateRole === "MID" && (enemyRoleTraits.damageAP > 1.15 || enemyRoleTraits.fragileCarry > 0.7 || enemyRoleTraits.pick > 0.8)) {
      addNeed("laneCounterMid", "can pressure the enemy MID", 18, { dive: 1, pick: 1, mobility: 0.75, damageAD: 0.35, control: 0.2, versatility: 0.35 });
    }
    if ((candidateRole === "TOP" || candidateRole === "JGL") && enemyRoleTraits.frontline > 1.55) {
      addNeed("laneCounterFrontline", `can punish the enemy ${candidateRole} frontline`, 13, { antiTank: 1, damageAD: 0.55, damageAP: 0.35, dive: 0.2, versatility: 0.25 });
    }
    if ((candidateRole === "JGL" || candidateRole === "SUP") && (enemyRoleTraits.dive + enemyRoleTraits.mobility) > 1.85) {
      addNeed("laneControlDive", `can control the enemy ${candidateRole} pressure`, 12, { control: 1, antiDive: 0.85, peel: 0.35, versatility: 0.25 });
    }
    if (candidateRole === "SUP" && enemyRoleTraits.control > 1.1 && ownTraits.peel < 2.1) {
      addNeed("supportAnswer", "can stabilize the support matchup", 10, { peel: 1, antiDive: 0.55, control: 0.4, utility: 0.35, versatility: 0.25 });
    }
    if (candidateRole === "ADC" && enemyRoleTraits.fragileCarry > 1.1) {
      addNeed("adcDpsRace", "can contest the enemy ADC", 9, { damageAD: 1, antiTank: 0.45, mobility: 0.25, versatility: 0.2 });
    }
  }

  return needs;
};

const getNeedMatchScore = (traits, matches) => Object.entries(matches || {}).reduce((score, [trait, weight]) => (
  score + Math.min(2.5, Number(traits?.[trait] || 0)) * Number(weight || 0)
), 0);

const getTraitSimilarity = (a = {}, b = {}) => {
  const keys = ["frontline", "engage", "peel", "antiTank", "damageAD", "damageAP", "dive", "pick", "mobility", "control", "utility", "fragileCarry"];
  const shared = keys.reduce((sum, key) => sum + Math.min(Number(a[key] || 0), Number(b[key] || 0)), 0);
  const total = keys.reduce((sum, key) => sum + Math.max(Number(a[key] || 0), Number(b[key] || 0)), 0);
  return total ? shared / total : 0;
};

const getReliableWinrate = (winrate, games, targetGames = 6) => {
  const safeGames = Math.max(0, Number(games || 0));
  const target = Math.max(1, Number(targetGames || 6));
  const confidence = safeGames / (safeGames + target);
  return 50 + (Number(winrate || 50) - 50) * confidence;
};

const getMatchupExperiencePayload = (entry, dataSide, role, enemyRoleProfile, championMeta) => {
  if (!enemyRoleProfile?.traits) return { score: 0, reasons: [], samples: [] };
  const roleRefs = role ? getEntryRefs(entry, dataSide, role, { source: "recent" }) : [];
  const refs = roleRefs.length ? roleRefs : getEntryRefs(entry, dataSide, null, { source: "recent" });
  const samples = refs.flatMap((ref) => ref.matchups || [])
    .map((matchup) => {
      const opponentTraits = getDraftTraitsForChampion({ name: matchup.champion, refs: [] }, dataSide, matchup.role || role, championMeta);
      const similarity = getTraitSimilarity(opponentTraits, enemyRoleProfile.traits);
      const impactSignal = Number(matchup.impactScore || 0);
      const resultSignal = matchup.win ? 1 : -0.65;
      return {
        ...matchup,
        similarity,
        value: similarity * (resultSignal + impactSignal * 0.8),
      };
    })
    .filter((sample) => sample.similarity > 0.34);

  if (!samples.length) return { score: 0, reasons: [], samples: [] };
  const averageValue = averageNumbers(samples.map((sample) => sample.value));
  const score = Math.max(-8, Math.min(10, averageValue * 7));
  const strongSamples = samples.filter((sample) => sample.value > 0.45).length;
  const weakSamples = samples.filter((sample) => sample.value < -0.25).length;
  const reasons = [];
  if (score > 1.5) reasons.push(`has positive recent experience into similar ${normalizeDraftRole(role) || "lane"} matchups`);
  if (score < -1.5) reasons.push(`has struggled recently into similar ${normalizeDraftRole(role) || "lane"} matchups`);
  return {
    score,
    reasons,
    samples,
    summary: { strongSamples, weakSamples },
  };
};

const getDraftPerformanceScore = (entry, dataSide, role = null, weights = DEFAULT_DRAFT_BOT_WEIGHTS, championMeta = {}, scoreFormula = "balanced") => {
  const scoring = { ...DEFAULT_DRAFT_BOT_WEIGHTS, ...(weights || {}) };
  const sideRefs = getEntryRefs(entry, dataSide);
  const roleRefs = role ? getEntryRefs(entry, dataSide, role) : sideRefs;
  const refs = roleRefs.length ? roleRefs : sideRefs;
  if (!refs.length) return -9999;

  const recentRefs = refs.filter((ref) => ref.source === "recent");
  const masteryRefs = refs.filter((ref) => ref.source === "mastery");
  const games = recentRefs.reduce((sum, ref) => sum + Number(ref.games || 0), 0);
  const weighted = recentRefs.reduce((acc, ref) => {
    const refGames = Number(ref.games || 0);
    if (!refGames || ref.winrate === null || ref.winrate === undefined) return acc;
    return { games: acc.games + refGames, total: acc.total + Number(ref.winrate) * refGames };
  }, { games: 0, total: 0 });
  const winrate = weighted.games ? weighted.total / weighted.games : 50;
  const reliableWinrate = getReliableWinrate(winrate, games, scoring.sampleTargetGames);
  const rawMasteryPoints = masteryRefs.reduce((max, ref) => Math.max(max, Number(ref.masteryPoints || 0)), 0);
  const masteryPoints = capMasteryPointsForChampion(rawMasteryPoints, entry, championMeta);
  const masteryLevel = masteryRefs.reduce((max, ref) => Math.max(max, Number(ref.masteryLevel || 0)), 0);
  const masteryScore = masteryPoints ? Math.log10(masteryPoints + 1) * Number(scoring.masteryPoints || 0) : 0;
  const roleFit = role ? (roleRefs.length ? Number(scoring.roleFit || 0) : -Math.abs(Number(scoring.offRolePenalty || 0))) : 0;
  const tags = getChampionMeta(championMeta, entry?.name).tags || [];
  const versatilityBonus = tags.length > 1 && roleRefs.length ? 4 + Math.min(3, tags.length - 1) : 0;
  const gamesScore = scoreFormula === "reliableGames"
    ? Math.log1p(games) * Number(scoring.sampleSize || 0)
    : games * Number(scoring.recentGames || 0);
  const winrateScore = scoreFormula === "reliableGames"
    ? ((reliableWinrate - 50) * Number(scoring.winrate || 0))
    : ((winrate - 50) * Number(scoring.winrate || 0));

  return (
    gamesScore
    + winrateScore
    + masteryScore
    + (masteryLevel * Number(scoring.masteryLevel || 0))
    + (recentRefs.length ? Number(scoring.recentBonus || 0) : 0)
    + roleFit
    + versatilityBonus
  );
};

const getBestRoleForEntry = (entry, dataSide, allowedRoles = ROLES_ORDER, weights = DEFAULT_DRAFT_BOT_WEIGHTS, championMeta = {}, scoreFormula = "balanced") => {
  return allowedRoles
    .map((role) => ({ role, score: getDraftPerformanceScore(entry, dataSide, role, weights, championMeta, scoreFormula) }))
    .sort((a, b) => b.score - a.score)[0]?.role || allowedRoles[0] || "ROLE";
};

const getDraftReasonDetails = (entry, dataSide, role = null) => {
  const strictRoleRefs = role ? getEntryRefs(entry, dataSide, role, { source: "recent", strictLane: true }) : [];
  const refs = strictRoleRefs.length
    ? strictRoleRefs
    : role ? getEntryRefs(entry, dataSide, role) : getEntryRefs(entry, dataSide);
  const usableRefs = refs.length ? refs : getEntryRefs(entry, dataSide);
  const recentRefs = usableRefs.filter((ref) => ref.source === "recent");
  const masteryRefs = usableRefs.filter((ref) => ref.source === "mastery");
  const games = recentRefs.reduce((sum, ref) => sum + Number(ref.games || 0), 0);
  const weighted = recentRefs.reduce((acc, ref) => {
    const refGames = Number(ref.games || 0);
    if (!refGames || ref.winrate === null || ref.winrate === undefined) return acc;
    return { games: acc.games + refGames, total: acc.total + Number(ref.winrate) * refGames };
  }, { games: 0, total: 0 });
  const winrate = weighted.games ? Math.round(weighted.total / weighted.games) : null;
  const masteryPoints = masteryRefs.reduce((max, ref) => Math.max(max, Number(ref.masteryPoints || 0)), 0);
  const details = [];
  if (role) details.push(`good ${role} fit`);
  if (games) details.push(`${games} recent game${games > 1 ? "s" : ""}`);
  if (winrate !== null && winrate >= 55) details.push(`${winrate}% recent winrate`);
  if (masteryPoints >= 100000) details.push("high mastery");
  if (!details.length) details.push("the best available performance signal");
  return details.join(", ");
};

function DraftPlanPanel({ allyPlayers, enemyPlayers, ddVersion, theme: C }) {
  const roleAdjustedAllyPlayers = useMemo(() => allyPlayers.map((player, index) => ({
    ...player,
    role: normalizeDraftRole(player?.role) || ROLES_ORDER[index % ROLES_ORDER.length],
  })), [allyPlayers]);
  const roleAdjustedEnemyPlayers = useMemo(() => enemyPlayers.map((player, index) => ({
    ...player,
    role: normalizeDraftRole(player?.role) || ROLES_ORDER[index % ROLES_ORDER.length],
  })), [enemyPlayers]);
  const championPool = useMemo(() => buildDraftChampionPool(roleAdjustedAllyPlayers, roleAdjustedEnemyPlayers), [roleAdjustedAllyPlayers, roleAdjustedEnemyPlayers]);
  const championMap = useMemo(() => Object.fromEntries(championPool.map((entry) => [entry.name, entry])), [championPool]);
  const playerOptions = useMemo(() => {
    const options = [];
    roleAdjustedAllyPlayers.forEach((player, index) => options.push({ key: `ally-${index}`, label: `Team One - ${getPlayerName(player)}` }));
    roleAdjustedEnemyPlayers.forEach((player, index) => options.push({ key: `enemy-${index}`, label: `Team Two - ${getPlayerName(player)}` }));
    return options;
  }, [roleAdjustedAllyPlayers, roleAdjustedEnemyPlayers]);

  const [drafts, setDrafts] = useState([createEmptyDraftPlan(0)]);
  const [activeDraftIndex, setActiveDraftIndex] = useState(0);
  const [activeSlot, setActiveSlot] = useState({ group: "bluePicks", index: 0 });
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [playerFilter, setPlayerFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [signalFilter, setSignalFilter] = useState("all");
  const [showBotSettings, setShowBotSettings] = useState(false);
  const [activeBotSettingsSide, setActiveBotSettingsSide] = useState("ally");
  const [fearlessMode, setFearlessMode] = useState(false);
  const [botConfigs, setBotConfigs] = useState({
    ally: createDraftBotConfig(),
    enemy: createDraftBotConfig(),
  });
  const [draftControlMode, setDraftControlMode] = useState("teamOne");
  const [championMeta, setChampionMeta] = useState({});
  const [draftRuntime, setDraftRuntime] = useState({
    started: false,
    stepIndex: 0,
    botThinking: false,
    log: [],
    roleAssignments: {},
  });

  const activeDraft = drafts[activeDraftIndex] || drafts[0] || createEmptyDraftPlan(0);
  const activeDraftUsedChampions = new Set(DRAFT_SLOT_KEYS.flatMap((key) => activeDraft[key] || []).filter(Boolean));
  const fearlessLockedChampions = useMemo(() => {
    if (!fearlessMode) return new Set();
    return new Set(
      drafts
        .slice(0, activeDraftIndex)
        .flatMap((draft) => [...(draft.bluePicks || []), ...(draft.redPicks || [])])
        .filter(Boolean)
    );
  }, [drafts, activeDraftIndex, fearlessMode]);
  const usedChampions = new Set([...activeDraftUsedChampions, ...fearlessLockedChampions]);
  const currentStep = draftRuntime.started ? DRAFT_SEQUENCE[draftRuntime.stepIndex] : null;
  const draftComplete = draftRuntime.started && draftRuntime.stepIndex >= DRAFT_SEQUENCE.length;

  useEffect(() => {
    let cancelled = false;
    fetch(`https://ddragon.leagueoflegends.com/cdn/${ddVersion}/data/en_US/champion.json`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setChampionMeta(buildChampionMetaLookup(data));
      })
      .catch(() => {
        if (!cancelled) setChampionMeta({});
      });
    return () => { cancelled = true; };
  }, [ddVersion]);

  const updateActiveDraft = (updater) => {
    setDrafts((prev) => prev.map((draft, index) => (index === activeDraftIndex ? updater(draft) : draft)));
  };

  const setTeamOneSide = (side) => {
    updateActiveDraft((draft) => ({ ...draft, teamOneSide: side }));
  };

  const updateBotWeight = (key, value) => {
    const parsed = Number(value);
    setBotConfigs((prev) => ({
      ...prev,
      [activeBotSettingsSide]: {
        ...prev[activeBotSettingsSide],
        weights: {
          ...(prev[activeBotSettingsSide]?.weights || DEFAULT_DRAFT_BOT_WEIGHTS),
          [key]: Number.isFinite(parsed) ? parsed : prev[activeBotSettingsSide]?.weights?.[key] ?? DEFAULT_DRAFT_BOT_WEIGHTS[key],
        },
      },
    }));
  };

  const updateActiveBotConfig = (patch) => {
    setBotConfigs((prev) => ({
      ...prev,
      [activeBotSettingsSide]: {
        ...prev[activeBotSettingsSide],
        ...patch,
      },
    }));
  };

  const resetBotWeights = () => {
    setBotConfigs((prev) => ({
      ...prev,
      [activeBotSettingsSide]: createDraftBotConfig(),
    }));
  };

  const toggleActiveBotComposition = () => {
    setBotConfigs((prev) => ({
      ...prev,
      [activeBotSettingsSide]: {
        ...prev[activeBotSettingsSide],
        useCompositionLogic: prev[activeBotSettingsSide]?.useCompositionLogic === false,
      },
    }));
  };

  const copyActiveBotSettingsToOther = () => {
    const targetSide = activeBotSettingsSide === "ally" ? "enemy" : "ally";
    setBotConfigs((prev) => ({
      ...prev,
      [targetSide]: {
        useCompositionLogic: prev[activeBotSettingsSide]?.useCompositionLogic !== false,
        scoreFormula: prev[activeBotSettingsSide]?.scoreFormula || "balanced",
        strategyMode: prev[activeBotSettingsSide]?.strategyMode || "adaptive",
        weights: { ...(prev[activeBotSettingsSide]?.weights || DEFAULT_DRAFT_BOT_WEIGHTS) },
      },
    }));
  };

  const addDraft = () => {
    const nextIndex = drafts.length;
    setDrafts((prev) => [...prev, createEmptyDraftPlan(prev.length)]);
    setActiveDraftIndex(nextIndex);
    setActiveSlot({ group: "bluePicks", index: 0 });
    setDraftRuntime({ started: false, stepIndex: 0, botThinking: false, log: [], roleAssignments: {} });
  };

  const clearSlot = (group, index) => {
    if (draftRuntime.started) return;
    updateActiveDraft((draft) => ({
      ...draft,
      [group]: draft[group].map((champion, slotIndex) => (slotIndex === index ? null : champion)),
    }));
  };

  const placeChampion = (championName) => {
    if (!championName || !activeSlot?.group) return;
    updateActiveDraft((draft) => {
      const cleaned = { ...draft };
      DRAFT_SLOT_KEYS.forEach((key) => {
        cleaned[key] = (draft[key] || []).map((champion) => (champion === championName ? null : champion));
      });
      cleaned[activeSlot.group] = (cleaned[activeSlot.group] || []).map((champion, index) => (
        index === activeSlot.index ? championName : champion
      ));
      return cleaned;
    });
  };

  const getSideTeamLabel = (side) => {
    const teamOneIsSide = activeDraft.teamOneSide === side;
    return teamOneIsSide ? "Team One" : "Team Two";
  };

  const getSideTeamColor = (side) => (getSideTeamLabel(side) === "Team One" ? C.team1 : C.team2);

  const getDataSideForBoardSide = (side) => (getSideTeamLabel(side) === "Team One" ? "ally" : "enemy");
  const getBoardSideForDataSide = (dataSide) => (
    dataSide === "ally" ? activeDraft.teamOneSide : activeDraft.teamOneSide === "blue" ? "red" : "blue"
  );
  const getBotConfigForDataSide = (dataSide) => botConfigs[dataSide] || createDraftBotConfig();
  const getBotWeightsForDataSide = (dataSide) => getBotConfigForDataSide(dataSide).weights || DEFAULT_DRAFT_BOT_WEIGHTS;
  const getBotScoreFormulaForDataSide = (dataSide) => getBotConfigForDataSide(dataSide).scoreFormula || "balanced";
  const getBotStrategyModeForDataSide = (dataSide) => getBotConfigForDataSide(dataSide).strategyMode || "adaptive";
  const getCompositionEnabledForDataSide = (dataSide) => getBotConfigForDataSide(dataSide).useCompositionLogic !== false;
  const activeBotConfig = getBotConfigForDataSide(activeBotSettingsSide);
  const activeBotWeights = activeBotConfig.weights || DEFAULT_DRAFT_BOT_WEIGHTS;
  const activeCompositionLogic = activeBotConfig.useCompositionLogic !== false;
  const activeBotColor = activeBotSettingsSide === "ally" ? C.team1 : C.team2;
  const activeScoreFormula = activeBotConfig.scoreFormula || "balanced";
  const activeStrategyMode = activeBotConfig.strategyMode || "adaptive";
  const humanDataSide = draftControlMode === "teamOne" ? "ally" : draftControlMode === "teamTwo" ? "enemy" : null;
  const humanSide = humanDataSide ? getBoardSideForDataSide(humanDataSide) : null;
  const currentActor = currentStep
    ? currentStep.side === humanSide ? "human" : "bot"
    : draftComplete ? "complete" : "idle";

  const getSideRoleRecentRefs = (dataSide, role) => (
    championPool.flatMap((entry) => (
      getEntryRefs(entry, dataSide, role, { source: "recent" }).map((ref) => ({ ...ref, champion: entry.name }))
    ))
  );

  const getRolePoolProfile = (dataSide, role, weights = DEFAULT_DRAFT_BOT_WEIGHTS) => {
    const refs = getSideRoleRecentRefs(dataSide, role);
    const totalGames = refs.reduce((sum, ref) => sum + Number(ref.games || 0), 0);
    const championMapByName = refs.reduce((map, ref) => {
      const champion = ref.champion;
      if (!champion) return map;
      if (!map[champion]) map[champion] = { champion, games: 0, wins: 0, weightedWr: 0 };
      const games = Number(ref.games || 0);
      map[champion].games += games;
      map[champion].wins += Number(ref.wins || 0);
      if (Number.isFinite(Number(ref.winrate))) map[champion].weightedWr += Number(ref.winrate) * games;
      return map;
    }, {});
    const champions = Object.values(championMapByName)
      .map((champion) => ({
        ...champion,
        winrate: champion.games ? Math.round(champion.weightedWr / champion.games) : 50,
      }))
      .sort((a, b) => b.games - a.games);
    const weighted = refs.reduce((acc, ref) => {
      const games = Number(ref.games || 0);
      if (!games || ref.winrate === null || ref.winrate === undefined) return acc;
      return { games: acc.games + games, total: acc.total + Number(ref.winrate) * games };
    }, { games: 0, total: 0 });
    const winrate = weighted.games ? weighted.total / weighted.games : 50;
    const reliableWinrate = getReliableWinrate(winrate, totalGames, weights.sampleTargetGames);
    const main = champions[0] || null;
    const mainShare = totalGames ? Number(main?.games || 0) / totalGames : 0;
    const championCount = champions.length;
    const depthScore = Math.min(4, championCount) * 2.4;
    const experienceScore = Math.log1p(totalGames) * 4;
    const score = (reliableWinrate - 50) + depthScore + experienceScore + (mainShare >= 0.5 ? 3 : 0) - (championCount <= 1 ? 5 : 0);
    return {
      role,
      refs,
      totalGames,
      championCount,
      champions,
      mainChampion: main?.champion || "",
      mainGames: Number(main?.games || 0),
      mainShare,
      winrate,
      reliableWinrate,
      score,
      isWeak: totalGames < 3 || championCount <= 1 || score < 7,
      isStrong: totalGames >= 4 && score >= 14,
      isMainHeavy: mainShare >= 0.48 && totalGames >= 3,
    };
  };

  const getTeamPoolProfile = (dataSide, weights = DEFAULT_DRAFT_BOT_WEIGHTS) => {
    const roles = ROLES_ORDER.map((role) => getRolePoolProfile(dataSide, role, weights));
    const averageScore = averageNumbers(roles.map((profile) => profile.score));
    const weakRoles = roles.filter((profile) => profile.isWeak).map((profile) => profile.role);
    const strongRoles = roles.filter((profile) => profile.isStrong).map((profile) => profile.role);
    return { roles, averageScore, weakRoles, strongRoles };
  };

  const getCandidatePoolPayload = (entry, dataSide, role, weights = DEFAULT_DRAFT_BOT_WEIGHTS) => {
    const profile = getRolePoolProfile(dataSide, role, weights);
    const refs = getEntryRefs(entry, dataSide, role, { source: "recent" });
    const champGames = refs.reduce((sum, ref) => sum + Number(ref.games || 0), 0);
    const champShare = profile.totalGames ? champGames / profile.totalGames : 0;
    const isMain = profile.mainChampion === entry?.name || champShare >= 0.45;
    const poolWeight = Number(weights.poolRead || 0);
    const mainWeight = Number(weights.mainChampion || 0);
    const score = (isMain ? mainWeight : champShare >= 0.25 ? mainWeight * 0.35 : 0)
      + (profile.isWeak ? -3 * poolWeight : 0)
      + (profile.championCount >= 3 ? 2 * poolWeight : 0);
    const reasons = [];
    if (isMain) reasons.push("is one of this player's main champions");
    if (profile.isWeak) reasons.push("comes from a limited champion pool");
    if (profile.championCount >= 3) reasons.push("fits a deeper champion pool");
    return { score, reasons, profile, isMain, champGames, champShare };
  };

  const getStrategyPayload = (entry, boardSide, dataSide, role, candidateTraits, mode, weights = DEFAULT_DRAFT_BOT_WEIGHTS, strategyMode = "adaptive", strategyDataSide = dataSide) => {
    if (strategyMode === "neutral") return { score: 0, reasons: [], mode: "neutral" };
    const ownDataSide = mode === "ban" ? strategyDataSide : dataSide;
    const targetDataSide = mode === "ban" ? dataSide : ownDataSide === "ally" ? "enemy" : "ally";
    const ownTeam = getTeamPoolProfile(ownDataSide, weights);
    const enemyTeam = getTeamPoolProfile(targetDataSide, weights);
    const ownLane = getRolePoolProfile(ownDataSide, role, weights);
    const enemyLane = getRolePoolProfile(targetDataSide, role, weights);
    const teamDelta = ownTeam.averageScore - enemyTeam.averageScore;
    const laneDelta = ownLane.score - enemyLane.score;
    const resolvedMode = strategyMode === "adaptive"
      ? teamDelta >= 4 || laneDelta >= 6 ? "diveIn" : teamDelta <= -4 || laneDelta <= -6 ? "counterClassic" : "balanced"
      : strategyMode;
    if (resolvedMode === "balanced") return { score: 0, reasons: [], mode: resolvedMode, ownLane, enemyLane, teamDelta, laneDelta };

    const pool = getCandidatePoolPayload(entry, dataSide, role, weights);
    const strategyWeight = Number(weights.teamStrategy || 0);
    const pressureScore = (
      Number(candidateTraits.dive || 0)
      + Number(candidateTraits.pick || 0)
      + Number(candidateTraits.mobility || 0) * 0.7
      + Number(candidateTraits.damageAD || 0) * 0.35
      + Number(candidateTraits.damageAP || 0) * 0.35
    );
    const stableScore = (
      Number(candidateTraits.peel || 0)
      + Number(candidateTraits.control || 0)
      + Number(candidateTraits.antiDive || 0)
      + Number(candidateTraits.frontline || 0) * 0.45
      + Number(candidateTraits.versatility || 0) * 0.35
    );

    if (mode === "ban") {
      const targetPool = getCandidatePoolPayload(entry, targetDataSide, role, weights);
      const enemyRoleIsStrong = enemyLane.isStrong || laneDelta < -5;
      const enemyRoleIsWeak = enemyLane.isWeak || laneDelta > 5;
      const banMainScore = targetPool.isMain ? Number(weights.mainChampion || 0) : 0;
      const score = resolvedMode === "diveIn"
        ? ((enemyRoleIsWeak ? 8 : 0) + banMainScore * 0.85) * strategyWeight
        : ((enemyRoleIsStrong ? 9 : 0) + banMainScore) * strategyWeight;
      const reasons = [];
      if (resolvedMode === "diveIn" && enemyRoleIsWeak) reasons.push(`keeps pressure on the weaker enemy ${role}`);
      if (resolvedMode === "counterClassic" && enemyRoleIsStrong) reasons.push(`denies a strong enemy ${role}`);
      if (targetPool.isMain) reasons.push("removes a main champion");
      return { score, reasons, mode: resolvedMode, ownLane, enemyLane, teamDelta, laneDelta };
    }

    const score = resolvedMode === "diveIn"
      ? (Math.max(0, laneDelta) * 0.65 + pressureScore * 5 + (pool.isMain ? 5 : 0)) * strategyWeight
      : (Math.max(0, -laneDelta) * 0.55 + stableScore * 5 + (pool.isMain ? 4 : 0)) * strategyWeight;
    const reasons = [];
    if (resolvedMode === "diveIn") {
      if (laneDelta > 4) reasons.push(`can attack a weaker enemy ${role}`);
      if (pressureScore > 2.4) reasons.push("fits a Dive In plan");
    } else {
      if (laneDelta < -4) reasons.push(`helps stabilize a difficult ${role} matchup`);
      if (stableScore > 2.4) reasons.push("fits a Counter Classic plan");
    }
    if (pool.isMain) reasons.push("uses a comfort/main pick");
    return { score, reasons, mode: resolvedMode, ownLane, enemyLane, teamDelta, laneDelta };
  };

  const getFilledRoles = (side, draft = activeDraft, assignments = draftRuntime.roleAssignments) => {
    const pickKey = `${side}Picks`;
    const dataSide = getDataSideForBoardSide(side);
    const weights = getBotWeightsForDataSide(dataSide);
    const formula = getBotScoreFormulaForDataSide(dataSide);
    return new Set((draft[pickKey] || []).map((champion, index) => {
      if (!champion) return null;
      const assignmentKey = `${pickKey}-${index}`;
      if (assignments[assignmentKey]) return assignments[assignmentKey];
      const entry = championMap[champion];
      return getBestRoleForEntry(entry, dataSide, ROLES_ORDER, weights, championMeta, formula);
    }).filter(Boolean));
  };

  const getPickProfilesForSide = (side, draft = activeDraft, assignments = draftRuntime.roleAssignments) => {
    const pickKey = `${side}Picks`;
    const dataSide = getDataSideForBoardSide(side);
    const weights = getBotWeightsForDataSide(dataSide);
    const formula = getBotScoreFormulaForDataSide(dataSide);
    return (draft[pickKey] || []).map((champion, index) => {
      if (!champion) return null;
      const entry = championMap[champion];
      if (!entry) return null;
      const assignmentKey = `${pickKey}-${index}`;
      const role = assignments[assignmentKey] || getBestRoleForEntry(entry, dataSide, ROLES_ORDER, weights, championMeta, formula);
      return {
        entry,
        role,
        traits: getDraftTraitsForChampion(entry, dataSide, role, championMeta),
      };
    }).filter(Boolean);
  };

  const getEnemyRoleProfileForSide = (boardSide, role) => {
    const enemySide = boardSide === "blue" ? "red" : "blue";
    return getPickProfilesForSide(enemySide).find((profile) => profile.role === normalizeDraftRole(role)) || null;
  };

  const getCompositionFitPayload = (entry, boardSide, role, mode = "pick", weights = DEFAULT_DRAFT_BOT_WEIGHTS, compositionEnabled = true) => {
    if (!entry || !compositionEnabled) return { score: 0, reasons: [], needs: [], enabled: false };
    const enemySide = boardSide === "blue" ? "red" : "blue";
    const dataSide = getDataSideForBoardSide(boardSide);
    const ownTraits = sumDraftTraits(getPickProfilesForSide(boardSide));
    const enemyTraits = sumDraftTraits(getPickProfilesForSide(enemySide));
    const candidateTraits = getDraftTraitsForChampion(entry, dataSide, role, championMeta);
    const ownProfiles = getPickProfilesForSide(boardSide);
    const ownRoles = new Set(ownProfiles.map((profile) => profile.role).filter(Boolean));
    const enemyRoleProfile = getEnemyRoleProfileForSide(boardSide, role);
    const needs = getTeamDraftNeeds(ownTraits, enemyTraits, {
      candidateRole: role,
      ownRoles,
      enemyRoleProfile,
    });
    const scoredNeeds = needs
      .map((need) => ({ ...need, match: getNeedMatchScore(candidateTraits, need.matches) }))
      .filter((need) => need.match > 0.35)
      .sort((a, b) => (b.match * b.weight) - (a.match * a.weight));
    const needScore = scoredNeeds.reduce((sum, need) => sum + need.match * need.weight, 0);
    const tags = getChampionMeta(championMeta, entry?.name).tags || [];
    const needsFrontlineAnswer = scoredNeeds.some((need) => ["frontline", "antiDive", "disengage"].includes(need.id));
    const alreadyHasFrontline = ownTraits.frontline > 3.1 && candidateTraits.frontline > 1.3 && (candidateTraits.damageAD + candidateTraits.damageAP + candidateTraits.peel) < 1.5;
    const soloTankWithoutClearNeed = tags.length === 1 && tags[0] === "Tank" && !needsFrontlineAnswer;
    const unsafeExtraCarry = ownTraits.fragileCarry > 1 && ownTraits.peel < 1.25 && candidateTraits.fragileCarry > 1;
    const penalty = (alreadyHasFrontline ? 10 : 0) + (soloTankWithoutClearNeed ? 8 : 0) + (unsafeExtraCarry ? 8 : 0);
    const reasonLabels = scoredNeeds.slice(0, 2).map((need) => need.label);
    const warnings = [];
    if (alreadyHasFrontline) warnings.push("already has enough frontline");
    if (soloTankWithoutClearNeed) warnings.push("no clear need for another pure tank");
    if (unsafeExtraCarry) warnings.push("needs protection before another fragile carry");
    const weight = mode === "ban" ? Number(weights.counterNeed || 0) : Number(weights.compositionNeed || 0);

    return {
      score: (needScore - penalty) * weight,
      reasons: reasonLabels,
      warnings,
      needs: scoredNeeds,
      enabled: true,
    };
  };

  const getDraftDecisionPayload = (entry, boardSide, dataSide, role, mode, botConfigDataSide = dataSide) => {
    const weights = getBotWeightsForDataSide(botConfigDataSide);
    const formula = getBotScoreFormulaForDataSide(botConfigDataSide);
    const strategyMode = getBotStrategyModeForDataSide(botConfigDataSide);
    const compositionEnabled = getCompositionEnabledForDataSide(botConfigDataSide);
    const candidateTraits = getDraftTraitsForChampion(entry, dataSide, role, championMeta);
    const performanceScore = getDraftPerformanceScore(entry, dataSide, role, weights, championMeta, formula);
    const composition = getCompositionFitPayload(entry, boardSide, role, mode, weights, compositionEnabled);
    const style = getPlayerStyleFitPayload(entry, dataSide, role, candidateTraits, weights);
    const matchup = getMatchupExperiencePayload(entry, dataSide, role, getEnemyRoleProfileForSide(boardSide, role), championMeta);
    const pool = getCandidatePoolPayload(entry, dataSide, role, weights);
    const strategy = getStrategyPayload(entry, boardSide, dataSide, role, candidateTraits, mode, weights, strategyMode, botConfigDataSide);
    return {
      score: performanceScore + composition.score + style.score + matchup.score + pool.score + strategy.score,
      performanceScore,
      composition,
      style,
      matchup,
      pool,
      strategy,
    };
  };

  const formatDecisionReason = (reason, mode = "pick") => {
    if (mode !== "ban") return reason;
    const cleanReason = String(reason || "")
      .replace(/^needs /, "")
      .replace(/^must answer /, "answer to ")
      .replace(/^can /, "")
      .replace(/^has /, "");
    if (/^removes a main champion/i.test(cleanReason)) return "a main champion";
    if (/^denies a strong enemy/i.test(cleanReason)) return cleanReason.replace(/^denies /i, "a ");
    if (/^keeps pressure on the weaker enemy/i.test(cleanReason)) return cleanReason.replace(/^keeps pressure on the weaker enemy /i, "comfort on weak enemy ");
    return cleanReason;
  };

  const getDecisionInsightText = (decision, mode = "pick") => [
    ...(decision?.composition?.enabled ? decision?.composition?.reasons || [] : []),
    ...(decision?.strategy?.reasons || []),
    ...(mode === "ban" ? [] : decision?.pool?.reasons || []),
    ...(mode === "ban" ? [] : decision?.style?.reasons || []),
    ...(mode === "ban" ? [] : decision?.matchup?.reasons || []),
  ].map((reason) => formatDecisionReason(reason, mode)).slice(0, 2).join(" and ");

  const chooseRoleForChampion = (entry, side) => {
    const dataSide = getDataSideForBoardSide(side);
    const weights = getBotWeightsForDataSide(dataSide);
    const formula = getBotScoreFormulaForDataSide(dataSide);
    const filledRoles = getFilledRoles(side);
    const openRoles = ROLES_ORDER.filter((role) => !filledRoles.has(role));
    const matchingOpenRole = openRoles.find((role) => {
      const strict = hasStrictLaneRecent(entry, dataSide, role);
      return strict || (!hasAnyLaneKnowledge(entry, dataSide) && getEntryRefs(entry, dataSide, role).length);
    });
    return matchingOpenRole || getBestRoleForEntry(entry, dataSide, openRoles.length ? openRoles : ROLES_ORDER, weights, championMeta, formula);
  };

  const resetDraftRuntime = () => {
    updateActiveDraft((draft) => ({
      ...createEmptyDraftPlan(activeDraftIndex),
      name: draft.name,
      teamOneSide: draft.teamOneSide,
    }));
    setActiveSlot({ group: "blueBans", index: 0 });
    setDraftRuntime({ started: false, stepIndex: 0, botThinking: false, log: [], roleAssignments: {} });
  };

  const startBotDraft = () => {
    const controlLabel = draftControlMode === "botVsBot"
      ? "Bot vs Bot mode."
      : `You control ${draftControlMode === "teamOne" ? "Team One" : "Team Two"}.`;
    updateActiveDraft((draft) => ({
      ...createEmptyDraftPlan(activeDraftIndex),
      name: draft.name,
      teamOneSide: draft.teamOneSide,
    }));
    setActiveSlot({ group: "blueBans", index: 0 });
    setDraftRuntime({
      started: true,
      stepIndex: 0,
      botThinking: false,
      log: [{ turn: 0, text: `Draft started. ${controlLabel}`, actor: "system" }],
      roleAssignments: {},
    });
  };

  const getDraftLogSection = (turn) => {
    if (!turn) return "System";
    if (turn <= 6) return "Bans 1";
    if (turn <= 12) return "Picks 1";
    if (turn <= 16) return "Bans 2";
    return "Picks 2";
  };

  const getLogItemMeta = (step) => {
    const dataSide = step ? getDataSideForBoardSide(step.side) : null;
    return {
      boardSide: step?.side || null,
      dataSide,
      teamLabel: step ? getSideTeamLabel(step.side) : "",
      section: getDraftLogSection(step?.turn || 0),
    };
  };

  const applyDraftAction = (entry, step, actor, role = null, reason = "") => {
    if (!entry || !step || usedChampions.has(entry.name)) return;
    updateActiveDraft((draft) => {
      const cleaned = { ...draft };
      DRAFT_SLOT_KEYS.forEach((key) => {
        cleaned[key] = (draft[key] || []).map((champion) => (champion === entry.name ? null : champion));
      });
      cleaned[step.group] = (cleaned[step.group] || []).map((champion, index) => (
        index === step.index ? entry.name : champion
      ));
      return cleaned;
    });

    setDraftRuntime((prev) => {
      const assignmentKey = `${step.group}-${step.index}`;
      const roleAssignments = step.action === "pick" && role
        ? { ...prev.roleAssignments, [assignmentKey]: role }
        : prev.roleAssignments;
      return {
        ...prev,
        stepIndex: Math.min(prev.stepIndex + 1, DRAFT_SEQUENCE.length),
        botThinking: false,
        roleAssignments,
        log: [
          {
            turn: step.turn,
            text: reason || `${actor === "bot" ? "Bot" : "You"} ${step.action === "ban" ? "banned" : "picked"} ${entry.name}.`,
            actor,
            champion: entry.name,
            action: step.action,
            ...getLogItemMeta(step),
          },
          ...prev.log,
        ],
      };
    });
  };

  const skipBotAction = (step, text) => {
    setDraftRuntime((prev) => ({
      ...prev,
      stepIndex: Math.min(prev.stepIndex + 1, DRAFT_SEQUENCE.length),
      botThinking: false,
      log: [
        { turn: step?.turn || 0, text, actor: "bot", action: step?.action || "skip", ...getLogItemMeta(step) },
        ...prev.log,
      ],
    }));
  };

  const pickBotChampion = () => {
    if (!currentStep || currentActor !== "bot") return;
    const botDataSide = getDataSideForBoardSide(currentStep.side);
    const botWeights = getBotWeightsForDataSide(botDataSide);
    const botFormula = getBotScoreFormulaForDataSide(botDataSide);
    const enemyDataSide = botDataSide === "ally" ? "enemy" : "ally";
    const available = championPool.filter((entry) => !usedChampions.has(entry.name));
    if (!available.length) {
      skipBotAction(currentStep, "Bot skipped this turn because no champion is available.");
      return;
    }

    if (currentStep.action === "ban") {
      const enemyBoardSide = getBoardSideForDataSide(enemyDataSide);
      const enemyFilledRoles = getFilledRoles(enemyBoardSide);
      const enemyOpenRoles = ROLES_ORDER.filter((role) => !enemyFilledRoles.has(role));
      const enemyHasLaneData = available.some((entry) => hasAnyLaneKnowledge(entry, enemyDataSide));
      const priorityRoles = enemyOpenRoles.length ? enemyOpenRoles : ROLES_ORDER;
      const laneCandidates = priorityRoles
        .flatMap((role) => getDraftRoleCandidates(available, enemyDataSide, role).map((entry) => ({ entry, role })));
      const fallbackCandidates = enemyHasLaneData
        ? []
        : available
          .filter((entry) => getEntryRefs(entry, enemyDataSide).length)
          .map((entry) => ({
            entry,
            role: getBestRoleForEntry(entry, enemyDataSide, priorityRoles, botWeights, championMeta, botFormula),
          }));
      const candidates = (laneCandidates.length ? laneCandidates : fallbackCandidates)
        .filter(({ entry, role }) => {
          const availableRoles = getAvailableBanRolesForEntry(entry, enemyDataSide, enemyOpenRoles);
          return availableRoles.length && (!role || availableRoles.includes(role));
        });
      const choicePayload = candidates
        .map(({ entry, role }) => {
          const decision = getDraftDecisionPayload(entry, enemyBoardSide, enemyDataSide, role, "ban", botDataSide);
          return { entry, role, score: decision.score, decision };
        })
        .sort((a, b) => b.score - a.score)[0];
      if (!choicePayload) {
        skipBotAction(currentStep, "Bot skipped this ban because no valid target was found on an open enemy role.");
        return;
      }
      const choice = choicePayload.entry;
      const enemyRole = choicePayload.role || getBestRoleForEntry(choice, enemyDataSide, priorityRoles, botWeights, championMeta, botFormula);
      const openRoleText = enemyOpenRoles.length ? "still open" : "available";
      const insightReason = getDecisionInsightText(choicePayload.decision, "ban");
      const reason = insightReason
        ? `Bot banned ${choice.name} to deny the enemy ${enemyRole} access to ${insightReason}; the role is ${openRoleText} and has ${getDraftReasonDetails(choice, enemyDataSide, enemyRole)}.`
        : `Bot banned ${choice.name} because the enemy ${enemyRole} is ${openRoleText} and has ${getDraftReasonDetails(choice, enemyDataSide, enemyRole)}.`;
      applyDraftAction(choice, currentStep, "bot", null, reason);
      return;
    }

    const filledRoles = getFilledRoles(currentStep.side);
    const openRoles = ROLES_ORDER.filter((role) => !filledRoles.has(role));
    const neededRole = openRoles[0] || ROLES_ORDER[0];
    const ownCandidates = available.filter((entry) => getEntryRefs(entry, botDataSide).length);
    const sideHasLaneData = ownCandidates.some((entry) => hasAnyLaneKnowledge(entry, botDataSide));
    const rolePools = openRoles
      .map((role) => ({ role, candidates: getDraftRoleCandidates(ownCandidates, botDataSide, role) }))
      .filter((pool) => pool.candidates.length);
    const selectedPool = rolePools.find((pool) => pool.role === neededRole) || rolePools[0];
    const fallbackCandidates = !sideHasLaneData ? ownCandidates : [];
    const candidatePayload = selectedPool
      ? selectedPool.candidates.map((entry) => ({ entry, role: selectedPool.role }))
      : fallbackCandidates.map((entry) => ({ entry, role: getBestRoleForEntry(entry, botDataSide, openRoles.length ? openRoles : ROLES_ORDER, botWeights, championMeta, botFormula) }));
    const choicePayload = candidatePayload
      .map(({ entry, role }) => {
        const decision = getDraftDecisionPayload(entry, currentStep.side, botDataSide, role, "pick", botDataSide);
        return { entry, role, score: decision.score, decision };
      })
      .sort((a, b) => b.score - a.score)[0];
    if (!choicePayload) {
      skipBotAction(currentStep, "Bot skipped this pick because no valid lane-specific champion was found.");
      return;
    }
    const choice = choicePayload.entry;
    const pickedRole = choicePayload.role || getBestRoleForEntry(choice, botDataSide, openRoles.length ? openRoles : ROLES_ORDER, botWeights, championMeta, botFormula);
    const insightReason = getDecisionInsightText(choicePayload.decision, "pick");
    const reason = insightReason
      ? `Bot picked ${choice.name} because Team ${getSideTeamLabel(currentStep.side) === "Team One" ? "One" : "Two"} ${insightReason}; the ${pickedRole} also has ${getDraftReasonDetails(choice, botDataSide, pickedRole)}.`
      : `Bot picked ${choice.name} because the ${pickedRole} has ${getDraftReasonDetails(choice, botDataSide, pickedRole)}.`;
    applyDraftAction(choice, currentStep, "bot", pickedRole, reason);
  };

  const handleChampionChoice = (entry) => {
    if (!entry || usedChampions.has(entry.name)) return;
    if (!draftRuntime.started) {
      placeChampion(entry.name);
      return;
    }
    if (!currentStep || currentActor !== "human") return;
    const role = currentStep.action === "pick" ? chooseRoleForChampion(entry, currentStep.side) : null;
    const reason = `You ${currentStep.action === "ban" ? "banned" : "picked"} ${entry.name}${role ? ` for ${role}` : ""}.`;
    applyDraftAction(entry, currentStep, "human", role, reason);
  };

  useEffect(() => {
    if (!draftRuntime.started || !currentStep) return;
    setActiveSlot({ group: currentStep.group, index: currentStep.index });
  }, [currentStep, draftRuntime.started]);

  useEffect(() => {
    if (!draftRuntime.started || draftComplete || currentActor !== "bot") return undefined;
    const thinkingTimer = window.setTimeout(() => {
      setDraftRuntime((prev) => ({ ...prev, botThinking: true }));
      window.setTimeout(() => pickBotChampion(), 450);
    }, 450);
    return () => window.clearTimeout(thinkingTimer);
    // The bot must play the exact turn captured when this timer starts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftRuntime.started, draftRuntime.stepIndex, draftComplete, currentActor]);

  const filteredChampions = championPool.filter((entry) => {
    const query = search.trim().toLowerCase();
    const signal = getDraftSignal(entry);
    if (query && !entry.name.toLowerCase().includes(query) && !entry.playerNames.join(" ").toLowerCase().includes(query)) return false;
    if (roleFilter !== "all" && !entry.roles.includes(roleFilter)) return false;
    if (playerFilter !== "all" && !entry.playerKeys.includes(playerFilter)) return false;
    if (teamFilter === "ally" && !entry.hasAlly) return false;
    if (teamFilter === "enemy" && !entry.hasEnemy) return false;
    if (sourceFilter === "recent" && !entry.isRecent) return false;
    if (sourceFilter === "mastery" && !entry.isMastery) return false;
    if (sourceFilter === "both" && (!entry.isRecent || !entry.isMastery)) return false;
    if (signalFilter !== "all" && signal !== signalFilter) return false;
    return true;
  });

  const getLogColors = (item) => {
    if (item.dataSide === "ally") return { color: C.team1, border: "rgba(74,222,128,0.36)", bg: "rgba(74,222,128,0.07)" };
    if (item.dataSide === "enemy") return { color: C.team2, border: "rgba(248,113,113,0.36)", bg: "rgba(248,113,113,0.07)" };
    return { color: C.muted, border: C.borderSub, bg: "rgba(39,39,42,0.38)" };
  };

  const getSlotTurn = (groupKey, index) => DRAFT_TURNS[groupKey]?.[index] || index + 1;

  const renderSlot = (groupKey, index, kind, side) => {
    const championName = activeDraft[groupKey]?.[index];
    const entry = championName ? championMap[championName] : null;
    const signal = getDraftSignal(entry);
    const signalStyle = getDraftSignalStyle(signal, C);
    const active = activeSlot.group === groupKey && activeSlot.index === index;
    const isBan = kind === "ban";
    const turn = getSlotTurn(groupKey, index);
    const offset = isBan ? 0 : (DRAFT_PICK_OFFSETS[groupKey]?.[index] || 0);
    const offsetStyle = !offset ? {} : side === "blue"
      ? { marginLeft: `${offset}px`, width: `calc(100% - ${offset}px)` }
      : { marginRight: `${offset}px`, width: `calc(100% - ${offset}px)` };

    return (
      <div key={`${groupKey}-${index}`} style={{ minWidth: 0, ...offsetStyle }}>
        <button
          type="button"
          onClick={() => { if (!draftRuntime.started) setActiveSlot({ group: groupKey, index }); }}
          title={entry ? `${entry.name} - ${signalStyle.label}` : `Turn ${turn}`}
          style={{
            position: "relative",
            width: "100%",
            minHeight: isBan ? "50px" : "66px",
            borderRadius: "9px",
            border: active ? `1px solid ${C.accentH}` : `1px solid ${entry ? signalStyle.border : C.borderSub}`,
            background: entry ? signalStyle.bg : "rgba(9,9,11,0.55)",
            color: "#f4f4f5",
            cursor: draftRuntime.started ? "default" : "pointer",
            padding: isBan ? "6px" : "8px",
            display: "grid",
            gridTemplateColumns: entry && !isBan ? draftRuntime.started ? "42px minmax(0,1fr)" : "42px minmax(0,1fr) 22px" : "1fr",
            gap: "8px",
            alignItems: "center",
            textAlign: "left",
            overflow: "hidden",
            boxShadow: active ? "0 0 0 1px rgba(96,165,250,0.35)" : "none",
          }}
        >
          <span style={{ position: "absolute", top: "5px", left: "6px", zIndex: 2, fontSize: "9px", color: active ? C.accentH : C.muted, fontWeight: "900", lineHeight: 1 }}>
            {turn}
          </span>
          {entry ? (
            isBan ? (
              <>
                <img
                  src={`https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/champion/${entry.name}.png`}
                  alt={entry.name}
                  style={{ width: "34px", height: "34px", objectFit: "cover", borderRadius: "7px", border: `1px solid ${signalStyle.border}`, justifySelf: "center" }}
                />
                {!draftRuntime.started && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); clearSlot(groupKey, index); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); clearSlot(groupKey, index); } }}
                    style={{ position: "absolute", top: "4px", right: "5px", width: "17px", height: "17px", borderRadius: "5px", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.42)", color: C.muted, fontSize: "10px", fontWeight: "900" }}
                  >
                    x
                  </span>
                )}
              </>
            ) : (
              <>
                <img
                  src={`https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/champion/${entry.name}.png`}
                  alt={entry.name}
                  style={{ width: "42px", height: "42px", objectFit: "cover", borderRadius: "8px", border: `1px solid ${signalStyle.border}` }}
                />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: "12px", fontWeight: "900", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.name}</span>
                  <span style={{ display: "block", fontSize: "10px", color: signalStyle.color, fontWeight: "800", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{signalStyle.label}</span>
                </span>
                {!draftRuntime.started && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); clearSlot(groupKey, index); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); clearSlot(groupKey, index); } }}
                    style={{ width: "22px", height: "22px", borderRadius: "6px", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.28)", color: C.muted, fontSize: "12px", fontWeight: "900" }}
                  >
                    x
                  </span>
                )}
              </>
            )
          ) : (
            <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", color: active ? C.accentH : C.muted, fontSize: isBan ? "10px" : "11px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.06em", paddingLeft: isBan ? "14px" : "16px" }}>
              <span>{isBan ? `Ban ${index + 1}` : `Pick ${index + 1}`}</span>
              {!isBan && <span>{active ? "Selected" : "Empty"}</span>}
            </span>
          )}
        </button>
      </div>
    );
  };

  const renderSide = (side) => {
    const color = getSideTeamColor(side);
    const isBlue = side === "blue";
    const bansKey = `${side}Bans`;
    const picksKey = `${side}Picks`;
    return (
      <div style={{ minWidth: 0, background: C.bg, border: `1px solid ${isBlue ? "rgba(96,165,250,0.35)" : "rgba(248,113,113,0.35)"}`, borderRadius: "12px", padding: "12px", boxShadow: "0 14px 35px rgba(0,0,0,0.24)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", gap: "8px" }}>
          <div>
            <div style={{ fontSize: "12px", color, fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.06em" }}>{isBlue ? "Blue side" : "Red side"}</div>
            <div style={{ fontSize: "11px", color: C.muted, marginTop: "2px" }}>{getSideTeamLabel(side)}</div>
          </div>
          <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: color, boxShadow: `0 0 14px ${color}` }} />
        </div>
        <div style={{ display: "grid", gap: "14px" }}>
          <div>
            <div style={{ color: C.muted, fontSize: "10px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "7px" }}>Bans</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "6px" }}>
              {Array.from({ length: 5 }).map((_, index) => renderSlot(bansKey, index, "ban", side))}
            </div>
          </div>
          <div>
            <div style={{ color: C.muted, fontSize: "10px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "7px" }}>Picks</div>
            <div style={{ display: "grid", gap: "8px" }}>
              {Array.from({ length: 5 }).map((_, index) => renderSlot(picksKey, index, "pick", side))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const botWeightRows = [
    ["recentGames", "Recent games", "Adds points for each recent game on the champion."],
    ["winrate", "Winrate", "Rewards recent winrate above 50%, punishes below 50%."],
    ["sampleSize", "Sample size", "Reliable WR only: rewards a stronger sample with log-based game count."],
    ["sampleTargetGames", "WR confidence target", "Reliable WR only: higher values require more games before trusting winrate."],
    ["masteryPoints", "Mastery points", "Adds a soft mastery score using log10(points)."],
    ["masteryLevel", "Mastery level", "Adds points for mastery level."],
    ["recentBonus", "Recent bonus", "Flat bonus when the champion appears in recent games."],
    ["roleFit", "Role fit", "Bonus when the champion matches the target role."],
    ["offRolePenalty", "Off-role penalty", "Penalty when the champion does not fit the target role."],
    ["compositionNeed", "Composition need", "When enabled, rewards champions that complete the bot team needs."],
    ["counterNeed", "Counter response", "When enabled, rewards bans that deny useful enemy composition answers."],
    ["playerStyle", "Player style", "Rewards champions that match the player's recent impact style and matchup experience."],
    ["mainChampion", "Main champion", "Rewards comfort picks and makes main champions stronger ban targets."],
    ["poolRead", "Pool read", "Reads champion pool depth, main dependency and weak lanes."],
    ["teamStrategy", "Team strategy", "Adds Dive In / Counter Classic lane and team strength logic."],
  ];

  const signalLegend = [
    ["ally", "Team One recent"],
    ["allyFlex", "Team One flex"],
    ["enemy", "Team Two recent"],
    ["enemyFlex", "Team Two flex"],
    ["contested", "Recent contested"],
    ["neutral", "Mastery only"],
  ];

  const botSettingTabs = [
    { id: "ally", label: "Team One bot", color: C.team1 },
    { id: "enemy", label: "Team Two bot", color: C.team2 },
  ];

  const draftControlOptions = [
    { id: "teamOne", label: "Play Team One", detail: "Bot controls Team Two." },
    { id: "teamTwo", label: "Play Team Two", detail: "Bot controls Team One." },
    { id: "botVsBot", label: "Bot vs Bot", detail: "Watch both bots draft." },
  ];

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "14px", flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0, color: "#f4f4f5", fontSize: "18px", fontWeight: "900" }}>Draft Plan</h3>
          <p style={{ margin: "6px 0 0", color: C.muted, fontSize: "13px", maxWidth: "720px", lineHeight: 1.5 }}>
            Build draft ideas from scout data. Champion colors use recent games only; mastery champions stay visible as neutral information.
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end", padding: "7px", border: `1px solid ${C.borderSub}`, borderRadius: "12px", background: "rgba(24,24,27,0.38)" }}>
          {drafts.map((draft, index) => (
            <button
              key={draft.name}
              type="button"
              onClick={() => setActiveDraftIndex(index)}
              style={{ padding: "8px 11px", borderRadius: "8px", border: index === activeDraftIndex ? `1px solid ${C.accent}` : `1px solid ${C.borderSub}`, background: index === activeDraftIndex ? "rgba(59,130,246,0.15)" : "transparent", color: index === activeDraftIndex ? C.accentH : C.muted, fontSize: "12px", fontWeight: "900", cursor: "pointer" }}
            >
              {draft.name}
            </button>
          ))}
          <button type="button" onClick={addDraft} style={{ padding: "8px 12px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.bg, color: "#f4f4f5", fontSize: "12px", fontWeight: "900", cursor: "pointer" }}>
            New draft
          </button>
          <button type="button" onClick={() => setFearlessMode((prev) => !prev)} style={{ padding: "8px 12px", borderRadius: "8px", border: fearlessMode ? `1px solid ${C.team1}` : `1px solid ${C.borderSub}`, background: fearlessMode ? "rgba(74,222,128,0.13)" : C.bg, color: fearlessMode ? C.team1 : C.muted, fontSize: "12px", fontWeight: "900", cursor: "pointer" }}>
            {fearlessMode ? `Fearless on (${fearlessLockedChampions.size})` : "Fearless off"}
          </button>
        </div>
      </div>

      <style>{`
        .draft-plan-layout {
          display: grid;
          grid-template-columns: minmax(250px, 0.76fr) minmax(340px, 1.18fr) minmax(250px, 0.76fr);
          gap: 14px;
          align-items: start;
        }
        .draft-champion-icons {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(52px, 1fr));
          gap: 8px;
          max-height: 610px;
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 4px;
        }
        @media (max-width: 1180px) {
          .draft-plan-layout { grid-template-columns: 1fr; }
          .draft-champion-icons { max-height: 420px; }
        }
      `}</style>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "12px" }}>
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: "12px", padding: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: "11px", color: C.muted, fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.08em" }}>Team One side</div>
            <div style={{ fontSize: "12px", color: "#f4f4f5", fontWeight: "800", marginTop: "3px" }}>{activeDraft.teamOneSide === "blue" ? "Team One starts blue" : "Team One starts red"}</div>
          </div>
          <div style={{ display: "flex", gap: "6px", padding: "3px", border: `1px solid ${C.border}`, borderRadius: "9px", background: C.surface }}>
            {["blue", "red"].map((side) => (
              <button
                key={side}
                type="button"
                onClick={() => setTeamOneSide(side)}
                disabled={draftRuntime.started}
                style={{ padding: "6px 10px", borderRadius: "7px", border: "none", background: activeDraft.teamOneSide === side ? C.accent : "transparent", color: activeDraft.teamOneSide === side ? "white" : C.muted, fontSize: "12px", fontWeight: "900", cursor: draftRuntime.started ? "default" : "pointer", opacity: draftRuntime.started && activeDraft.teamOneSide !== side ? 0.45 : 1 }}
              >
                {side === "blue" ? "Blue" : "Red"}
              </button>
            ))}
          </div>
        </div>

        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: "12px", padding: "12px", display: "grid", gap: "10px" }}>
          <div>
            <div style={{ fontSize: "11px", color: C.muted, fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.08em" }}>Draft control</div>
            <div style={{ fontSize: "12px", color: "#f4f4f5", fontWeight: "800", marginTop: "3px" }}>
              {draftControlOptions.find((option) => option.id === draftControlMode)?.detail}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "8px" }}>
            {draftControlOptions.map((option) => {
              const selected = draftControlMode === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setDraftControlMode(option.id)}
                  disabled={draftRuntime.started}
                  style={{ padding: "8px 10px", borderRadius: "9px", border: selected ? `1px solid ${C.accent}` : `1px solid ${C.borderSub}`, background: selected ? "rgba(59,130,246,0.15)" : C.surface, color: selected ? C.accentH : C.muted, fontSize: "11px", fontWeight: "900", cursor: draftRuntime.started ? "default" : "pointer", opacity: draftRuntime.started && !selected ? 0.48 : 1 }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap", paddingTop: "2px" }}>
            <span style={{ color: C.muted, fontSize: "11px", fontWeight: "800" }}>
              Bot setup lives here, below the draft plan header.
            </span>
            <button type="button" onClick={() => setShowBotSettings((prev) => !prev)} style={{ padding: "8px 12px", borderRadius: "8px", border: showBotSettings ? `1px solid ${C.accent}` : `1px solid ${C.borderSub}`, background: showBotSettings ? "rgba(59,130,246,0.15)" : C.surface, color: showBotSettings ? C.accentH : "#f4f4f5", fontSize: "12px", fontWeight: "900", cursor: "pointer" }}>
              {showBotSettings ? "Hide bot settings" : "Bot settings"}
            </button>
          </div>
        </div>
      </div>

      {showBotSettings && (
        <div style={{ background: C.bg, border: `1px solid rgba(96,165,250,0.28)`, borderRadius: "12px", padding: "12px", display: "grid", gap: "12px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "#f4f4f5", fontSize: "13px", fontWeight: "900" }}>Draft bot settings</div>
              <div style={{ color: C.muted, fontSize: "11px", marginTop: "2px" }}>Tune each bot independently. Useful for bot vs bot tests or for letting one bot play your team.</div>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button type="button" onClick={toggleActiveBotComposition} style={{ padding: "7px 10px", borderRadius: "8px", border: activeCompositionLogic ? `1px solid ${activeBotColor}` : `1px solid ${C.borderSub}`, background: activeCompositionLogic ? `${activeBotColor}1A` : "transparent", color: activeCompositionLogic ? activeBotColor : C.muted, fontSize: "11px", fontWeight: "900", cursor: "pointer" }}>
                {activeCompositionLogic ? "Composition logic on" : "Composition logic off"}
              </button>
              <button type="button" onClick={copyActiveBotSettingsToOther} style={{ padding: "7px 10px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: "transparent", color: C.muted, fontSize: "11px", fontWeight: "900", cursor: "pointer" }}>
                Copy to other bot
              </button>
              <button type="button" onClick={resetBotWeights} style={{ padding: "7px 10px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: "transparent", color: C.muted, fontSize: "11px", fontWeight: "900", cursor: "pointer" }}>
                Reset this bot
              </button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "8px" }}>
            {botSettingTabs.map((tab) => {
              const selected = activeBotSettingsSide === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveBotSettingsSide(tab.id)}
                  style={{ padding: "9px 10px", borderRadius: "9px", border: selected ? `1px solid ${tab.color}` : `1px solid ${C.borderSub}`, background: selected ? `${tab.color}1A` : C.surface, color: selected ? tab.color : C.muted, fontSize: "12px", fontWeight: "900", cursor: "pointer" }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "10px" }}>
            <label style={{ display: "grid", gap: "6px", minWidth: 0 }}>
              <span style={{ color: C.muted, fontSize: "10px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.06em" }}>Score formula</span>
              <select
                value={activeScoreFormula}
                onChange={(e) => updateActiveBotConfig({ scoreFormula: e.target.value })}
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 10px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.surface, color: "#f4f4f5", fontSize: "12px", fontWeight: "900", outline: "none" }}
              >
                {DRAFT_SCORE_FORMULAS.map((formula) => (
                  <option key={formula.id} value={formula.id}>{formula.label}</option>
                ))}
              </select>
              <span style={{ color: C.muted, fontSize: "10px", lineHeight: 1.35 }}>
                {DRAFT_SCORE_FORMULAS.find((formula) => formula.id === activeScoreFormula)?.detail}
              </span>
            </label>
            <label style={{ display: "grid", gap: "6px", minWidth: 0 }}>
              <span style={{ color: C.muted, fontSize: "10px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.06em" }}>Team strategy</span>
              <select
                value={activeStrategyMode}
                onChange={(e) => updateActiveBotConfig({ strategyMode: e.target.value })}
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 10px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.surface, color: "#f4f4f5", fontSize: "12px", fontWeight: "900", outline: "none" }}
              >
                {DRAFT_STRATEGY_MODES.map((strategy) => (
                  <option key={strategy.id} value={strategy.id}>{strategy.label}</option>
                ))}
              </select>
              <span style={{ color: C.muted, fontSize: "10px", lineHeight: 1.35 }}>
                {DRAFT_STRATEGY_MODES.find((strategy) => strategy.id === activeStrategyMode)?.detail}
              </span>
            </label>
          </div>
          <div style={{ border: `1px solid ${C.borderSub}`, background: "rgba(9,9,11,0.48)", borderRadius: "9px", padding: "9px 10px", color: C.muted, fontSize: "11px", lineHeight: 1.55 }}>
            <strong style={{ color: "#f4f4f5" }}>Score formula:</strong>{" "}
            {activeScoreFormula === "reliableGames"
              ? "Reliable WR = 50 + (WR - 50) x games / (games + target), then adds a stronger game-sample bonus. A 1-game 100% winrate will not beat a bigger sample too easily."
              : "Balanced uses raw recent games, raw winrate, capped mastery, role fit, player style and matchup experience."}
            {" "}<strong style={{ color: "#f4f4f5" }}>Strategy:</strong>{" "}
            {activeStrategyMode === "adaptive"
              ? "Adaptive reads both teams and lanes, then leans Dive In when your side is stronger or Counter Classic when it needs respect."
              : DRAFT_STRATEGY_MODES.find((strategy) => strategy.id === activeStrategyMode)?.detail}
            {activeCompositionLogic ? " Composition needs are active." : " Composition needs are disabled."}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "10px" }}>
            {botWeightRows.map(([key, label, description]) => (
              <label key={key} style={{ display: "grid", gap: "6px", minWidth: 0 }}>
                <span style={{ color: C.muted, fontSize: "10px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
                <input
                  type="number"
                  step={key === "winrate" ? "0.1" : "1"}
                  value={activeBotWeights[key]}
                  onChange={(e) => updateBotWeight(key, e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box", padding: "8px 9px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.surface, color: "#f4f4f5", fontSize: "12px", fontWeight: "800", outline: "none" }}
                />
                <span style={{ color: C.muted, fontSize: "10px", lineHeight: 1.35 }}>{description}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div style={{ background: draftRuntime.started ? "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(9,9,11,0.7))" : C.bg, border: `1px solid ${draftRuntime.started ? "rgba(96,165,250,0.36)" : C.border}`, borderRadius: "12px", padding: "12px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: "10px", color: C.muted, fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.08em" }}>Current turn</div>
          <div style={{ color: draftComplete ? C.team1 : "#f4f4f5", fontSize: "18px", fontWeight: "900", marginTop: "3px" }}>
            {!draftRuntime.started ? "Draft not started" : draftComplete ? "Draft complete" : currentStep?.label}
          </div>
        </div>
        <div style={{ color: C.muted, fontSize: "12px", lineHeight: 1.5 }}>
          {!draftRuntime.started && (draftControlMode === "botVsBot" ? "Click Start Draft to watch both bots play every turn automatically." : "Click Start Draft to begin from Blue ban 1. Nothing is picked or banned automatically before that.")}
          {draftRuntime.started && !draftComplete && currentActor === "human" && "Your turn. Select an available champion from the pool."}
          {draftRuntime.started && !draftComplete && currentActor === "bot" && (draftRuntime.botThinking ? "Bot is thinking..." : draftControlMode === "botVsBot" ? "Bot vs Bot is running automatically." : "Bot turn. It will play automatically.")}
          {draftComplete && "All picks and bans are locked for this draft."}
          {fearlessMode && <div style={{ marginTop: "4px", color: "#bae6fd", fontWeight: "800" }}>Fearless mode: previous draft picks are unavailable for players and bot.</div>}
        </div>
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button type="button" onClick={startBotDraft} style={{ padding: "9px 12px", borderRadius: "8px", border: "none", background: C.accent, color: "white", fontSize: "12px", fontWeight: "900", cursor: "pointer" }}>
            {draftRuntime.started ? "Restart Draft" : "Start Draft"}
          </button>
          {draftRuntime.started && (
            <button type="button" onClick={resetDraftRuntime} style={{ padding: "9px 12px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: "transparent", color: C.muted, fontSize: "12px", fontWeight: "900", cursor: "pointer" }}>
              Reset
            </button>
          )}
        </div>
      </div>

      <div className="draft-plan-layout">
        {renderSide("blue")}

        <div style={{ minWidth: 0, background: C.bg, border: `1px solid ${C.border}`, borderRadius: "12px", padding: "12px", display: "grid", gap: "12px", boxShadow: "0 14px 35px rgba(0,0,0,0.22)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "#f4f4f5", fontSize: "14px", fontWeight: "900" }}>Champion pool</div>
              <div style={{ color: C.muted, fontSize: "11px", marginTop: "2px" }}>{filteredChampions.length}/{championPool.length} champions visible</div>
            </div>
            <div style={{ display: "flex", gap: "7px", flexWrap: "wrap", justifyContent: "flex-end" }}>
              {signalLegend.map(([signal, label]) => {
                const style = getDraftSignalStyle(signal, C);
                return (
                  <span key={signal} style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: C.muted, fontSize: "10px", fontWeight: "800" }}>
                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: style.color, boxShadow: `0 0 7px ${style.color}` }} />
                    {label}
                  </span>
                );
              })}
            </div>
          </div>

          <div style={{ border: `1px solid ${C.borderSub}`, borderRadius: "11px", background: "rgba(24,24,27,0.42)", padding: "10px", display: "grid", gap: "9px" }}>
            <div style={{ color: C.muted, fontSize: "10px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.08em" }}>Filters</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: "8px" }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search champion or player"
              style={{ gridColumn: "span 2", minWidth: 0, padding: "9px 10px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.surface, color: "#f4f4f5", fontWeight: "700", fontSize: "12px" }}
            />
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} style={{ minWidth: 0, padding: "9px 10px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.surface, color: "#f4f4f5", fontWeight: "700", fontSize: "12px" }}>
              <option value="all">All roles</option>
              {ROLES_ORDER.map((role) => <option key={role} value={role}>{role}</option>)}
            </select>
            <select value={playerFilter} onChange={(e) => setPlayerFilter(e.target.value)} style={{ minWidth: 0, padding: "9px 10px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.surface, color: "#f4f4f5", fontWeight: "700", fontSize: "12px" }}>
              <option value="all">All players</option>
              {playerOptions.map((player) => <option key={player.key} value={player.key}>{player.label}</option>)}
            </select>
            <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} style={{ minWidth: 0, padding: "9px 10px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.surface, color: "#f4f4f5", fontWeight: "700", fontSize: "12px" }}>
              <option value="all">Both teams</option>
              <option value="ally">Team One</option>
              <option value="enemy">Team Two</option>
            </select>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} style={{ minWidth: 0, padding: "9px 10px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.surface, color: "#f4f4f5", fontWeight: "700", fontSize: "12px" }}>
              <option value="all">All sources</option>
              <option value="recent">Recent games</option>
              <option value="mastery">Mastery</option>
              <option value="both">Recent + mastery</option>
            </select>
            <select value={signalFilter} onChange={(e) => setSignalFilter(e.target.value)} style={{ minWidth: 0, padding: "9px 10px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.surface, color: "#f4f4f5", fontWeight: "700", fontSize: "12px" }}>
              <option value="all">All signals</option>
              <option value="ally">Team One recent</option>
              <option value="allyFlex">Team One flex</option>
              <option value="enemy">Team Two recent</option>
              <option value="enemyFlex">Team Two flex</option>
              <option value="contested">Recent contested</option>
              <option value="neutral">Mastery / known</option>
            </select>
            </div>
          </div>

          <div style={{ border: `1px solid ${C.borderSub}`, borderRadius: "11px", background: "rgba(9,9,11,0.32)", padding: "10px", display: "grid", gap: "9px" }}>
            <div style={{ color: C.muted, fontSize: "10px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.08em" }}>Champion selection</div>
            <div className="draft-champion-icons">
            {filteredChampions.map((entry) => {
              const signal = getDraftSignal(entry);
              const signalStyle = getDraftSignalStyle(signal, C);
              const activeUsed = activeDraftUsedChampions.has(entry.name);
              const lockedByFearless = fearlessLockedChampions.has(entry.name);
              const used = activeUsed || lockedByFearless;
              const lockedByTurn = draftRuntime.started && (currentActor !== "human" || draftComplete);
              const meta = getChampionMeta(championMeta, entry.name);
              const tags = meta.tags || [];
              return (
                <button
                  key={entry.name}
                  type="button"
                  onClick={() => handleChampionChoice(entry)}
                  disabled={used || lockedByTurn}
                  title={`${entry.name} - ${tags.length ? tags.join(" / ") : "No Riot tags"} - ${lockedByFearless ? "Locked by Fearless - " : ""}${signalStyle.label} - ${entry.playerNames.join(" / ")}`}
                  style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "1 / 1",
                    minWidth: 0,
                    borderRadius: "9px",
                    border: `1px solid ${signalStyle.border}`,
                    background: used ? lockedByFearless ? "rgba(14,116,144,0.18)" : "rgba(39,39,42,0.65)" : signalStyle.bg,
                    cursor: used || lockedByTurn ? "default" : "pointer",
                    padding: "4px",
                    opacity: used ? 0.34 : lockedByTurn ? 0.58 : 1,
                    boxShadow: used ? "inset 0 0 0 1px rgba(255,255,255,0.04)" : "none",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={`https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/champion/${entry.name}.png`}
                    alt={entry.name}
                    style={{ width: "100%", height: "100%", borderRadius: "7px", objectFit: "cover", display: "block" }}
                  />
                  {tags.length > 0 && (
                    <span style={{ position: "absolute", left: "5px", top: "5px", display: "flex", gap: "2px", zIndex: 2 }}>
                      {tags.slice(0, 2).map((tag) => (
                        <span key={tag} style={{ minWidth: "14px", padding: "1px 3px", borderRadius: "999px", background: "rgba(9,9,11,0.82)", border: "1px solid rgba(255,255,255,0.18)", color: "#f4f4f5", fontSize: "7px", lineHeight: 1.15, fontWeight: "900" }}>
                          {DRAFT_TAG_SHORT[tag] || tag.slice(0, 2)}
                        </span>
                      ))}
                    </span>
                  )}
                  <span style={{ position: "absolute", right: "5px", top: "5px", width: "9px", height: "9px", borderRadius: "50%", background: signalStyle.color, border: "1px solid rgba(0,0,0,0.55)", boxShadow: `0 0 8px ${signalStyle.color}` }} />
                  <span style={{ position: "absolute", left: "5px", bottom: "5px", display: "flex", gap: "3px" }}>
                    {entry.isRecent && <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: C.accentH }} />}
                    {entry.isMastery && <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#eab308" }} />}
                  </span>
                  {used && <span style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.48)" }} />}
                  {lockedByFearless && (
                    <span style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", zIndex: 3, padding: "3px 6px", borderRadius: "999px", border: "1px solid rgba(125,211,252,0.55)", background: "rgba(8,47,73,0.9)", color: "#bae6fd", fontSize: "8px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Fearless
                    </span>
                  )}
                </button>
              );
            })}
            {!filteredChampions.length && (
              <div style={{ gridColumn: "1 / -1", minHeight: "120px", border: `1px dashed ${C.borderSub}`, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: "12px", fontWeight: "800", textAlign: "center", padding: "16px" }}>
                No champion matches these filters.
              </div>
            )}
            </div>
          </div>
          {draftRuntime.log.length > 0 && (
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "10px", display: "grid", gap: "6px" }}>
              <div style={{ color: C.muted, fontSize: "10px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.08em" }}>Draft notes</div>
              <div style={{ maxHeight: "156px", overflowY: "auto", display: "grid", gap: "6px", paddingRight: "4px" }}>
                {draftRuntime.log.map((item, index) => {
                  const colors = getLogColors(item);
                  return (
                    <div key={`${item.turn}-${index}`} style={{ color: colors.color, fontSize: "11px", lineHeight: 1.45, fontWeight: "700" }}>
                      {item.turn ? `#${item.turn} ` : ""}{item.text}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {renderSide("red")}
      </div>
    </div>
  );
}
// -------------------------------------------------------------------------

export default function ScoutPage() {
  const location = useLocation();
  const { shareId } = useParams();
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [allyPlayers, setAllyPlayers] = useState([]);
  const [enemyPlayers, setEnemyPlayers] = useState([]);
  const [team1Url, setTeam1Url] = useState("");
  const [team2Url, setTeam2Url] = useState("");
  const [reliabilityOpen] = useState(false);
  const [reliabilityName, setReliabilityName] = useState("");
  const [reliabilityTag, setReliabilityTag] = useState("");
  const [reliabilityMatch, setReliabilityMatch] = useState(null);
  const [middleView, setMiddleView] = useState("impact");
  
  const [isValidating, setIsValidating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0); // NOUVEAU: Barre de progression
  const [loadingInfo, setLoadingInfo] = useState({ message: "", estimatedLabel: "", remainingLabel: "", apiEnvironment: "" });
  const [validatedTeams, setValidatedTeams] = useState(null);
  
  const [teamRoles, setTeamRoles] = useState({ team1: [], team2: [] });
  const [error, setError] = useState("");
  const [hasResult, setHasResult] = useState(false);
  const [selectedChamps, setSelectedChamps] = useState({ ally: {}, enemy: {} });
  const [activeCategories, setActiveCategories] = useState(["Dégâts", "Vision", "Early impact"]);
  const [ddVersion, setDdVersion] = useState("14.21.1");
  const [showCharts, setShowCharts] = useState(true);
  const [shareLoading, setShareLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareExpiresAt, setShareExpiresAt] = useState(null);
  const [experimentalLabReturn, setExperimentalLabReturn] = useState(null);
  const [draggedScoutCard, setDraggedScoutCard] = useState(null);
  const suppressNextCardClick = useRef(false);

  const players = [...allyPlayers, ...enemyPlayers];
  const isSharedView = Boolean(shareId);

  const updateProgress = (value) => {
    const next = Math.max(0, Math.min(100, Number(value || 0)));
    setProgress(prev => Math.max(prev, next));
  };

  useEffect(() => {
    fetch("https://ddragon.leagueoflegends.com/api/versions.json")
      .then(r => r.json())
      .then(v => setDdVersion(v[0]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!shareId && location.state?.restoreLastScout) {
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(LAST_SCOUT_STORAGE_KEY) || "null");
        const result = saved?.result || {};
        if ((result.team1 || []).length || (result.team2 || []).length) {
          setAllyPlayers(result.team1 || []);
          setEnemyPlayers(result.team2 || []);
          setSelectedChamps(result.selectedChamps || { ally: {}, enemy: {} });
          setActiveCategories(result.activeCategories || ["Dégâts", "Vision", "Early impact"]);
          setReliabilityMatch(result.reliabilityMatch || null);
          setMiddleView(result.reliabilityMatch ? "truth" : "impact");
          setExperimentalLabReturn(result.experimentalLabReturn || null);
          setValidatedTeams(null);
          setTeam1Url("");
          setTeam2Url("");
          setError("");
          setHasResult(true);
          return;
        }
      } catch (_) {}
    }

    if (!shareId && location.state?.experimentalScout) {
      const result = location.state.experimentalScout;
      setAllyPlayers(result.team1 || []);
      setEnemyPlayers(result.team2 || []);
      setSelectedChamps({ ally: {}, enemy: {} });
      setActiveCategories(Object.keys(CATEGORIZED_STATS));
      setReliabilityMatch(null);
      setMiddleView("impact");
      setValidatedTeams(null);
      setTeam1Url("");
      setTeam2Url("");
      setExperimentalLabReturn(result.labReturn || null);
      setHasResult(true);
      setError("");
      return;
    }
    if (!shareId && location.state?.useMock) {
      setAllyPlayers(mockTeam1);
      setEnemyPlayers(mockTeam2);
      setSelectedChamps({ ally: {}, enemy: {} });
      setExperimentalLabReturn(null);
      setHasResult(true);
    }
  }, [location, shareId]);

  useEffect(() => {
    if (!hasResult || (!allyPlayers.length && !enemyPlayers.length)) return;

    try {
      window.sessionStorage.setItem(LAST_SCOUT_STORAGE_KEY, JSON.stringify({
        savedAt: Date.now(),
        path: shareId ? `/scout/shared/${shareId}` : "/scout",
        result: {
          team1: allyPlayers,
          team2: enemyPlayers,
          selectedChamps,
          activeCategories,
          reliabilityMatch,
          experimentalLabReturn,
        },
      }));
    } catch (_) {}
  }, [activeCategories, allyPlayers, enemyPlayers, experimentalLabReturn, hasResult, reliabilityMatch, selectedChamps, shareId]);

  useEffect(() => {
    if (!shareId) return;

    let cancelled = false;
    const loadSharedScout = async () => {
      try {
        setShareLoading(true);
        setError("");
        setSelectedPlayer(null);
        setHasResult(false);

        const res = await fetch(`${API_BASE_URL}/scout/share/${shareId}`);
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error === "SHARE_EXPIRED"
            ? "Ce lien d'analyse a expire. Relance une analyse pour generer un nouveau lien."
            : data.error || "Impossible de charger cette analyse partagee");
        }

        if (cancelled) return;
        const result = data.result || {};
        setAllyPlayers(result.team1 || []);
        setEnemyPlayers(result.team2 || []);
        setSelectedChamps(result.selectedChamps || { ally: {}, enemy: {} });
        setActiveCategories(result.activeCategories || ["Dégâts", "Vision", "Early impact"]);
        setReliabilityMatch(result.reliabilityMatch || null);
        setMiddleView(result.reliabilityMatch ? "truth" : "impact");
        setShareExpiresAt(data.expiresAt || null);
        setValidatedTeams(null);
        setTeam1Url("");
        setTeam2Url("");
        setExperimentalLabReturn(null);
        setHasResult(true);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Lien partage invalide");
          setAllyPlayers([]);
          setEnemyPlayers([]);
          setHasResult(false);
        }
      } finally {
        if (!cancelled) setShareLoading(false);
      }
    };

    loadSharedScout();
    return () => { cancelled = true; };
  }, [shareId]);

  const handleValidate = async () => {
    try {
      setIsValidating(true); setError(""); setValidatedTeams(null);

      const res = await fetch(`${API_BASE_URL}/scout/validate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ team1_url: team1Url, team2_url: team2Url }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erreur lors de la validation");

      const initRoles = (teamData) => teamData.map((_, i) => ROLES_ORDER[i % 5]);
      setTeamRoles({ team1: initRoles(data.team1 || []), team2: initRoles(data.team2 || []) });
      setValidatedTeams(data);
    } catch (err) {
      setError(err.message || "Erreur inconnue");
    } finally {
      setIsValidating(false);
    }
  };

  const handleRoleChange = (teamKey, index, newRole) => {
    setTeamRoles(prev => {
      const newRoles = [...prev[teamKey]];
      const oldRole = newRoles[index];
      const existingIndex = newRoles.indexOf(newRole);
      
      if (existingIndex !== -1) newRoles[existingIndex] = oldRole;
      newRoles[index] = newRole;
      return { ...prev, [teamKey]: newRoles };
    });
  };

  const handleScout = async () => {
    try {
      setLoading(true); setError(""); setSelectedPlayer(null); setShareUrl(""); setShareExpiresAt(null);
      setSelectedChamps({ ally: {}, enemy: {} });
      setProgress(0);
      setLoadingInfo({ message: "Preparation de l'analyse", estimatedLabel: "", remainingLabel: "", apiEnvironment: "" });
      setReliabilityMatch(null);
      setMiddleView("impact");

      const startRes = await fetch(`${API_BASE_URL}/scout/start`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ team1_url: team1Url, team2_url: team2Url, count: 30 }),
      });

      const startData = await startRes.json();
      if (!startRes.ok) throw new Error(startData.error || "Erreur backend");

      setLoadingInfo({
        message: "Analyse demarree",
        estimatedLabel: startData.estimatedLabel || "",
        remainingLabel: startData.estimatedLabel || "",
        apiEnvironment: startData.apiEnvironment || "",
      });

      let status = startData;
      while (status.status !== "done") {
        await new Promise(resolve => setTimeout(resolve, 1000));

        const statusRes = await fetch(`${API_BASE_URL}/scout/status/${startData.jobId}`);
        const statusData = await statusRes.json();
        if (!statusRes.ok) throw new Error(statusData.error === "JOB_EXPIRED" ? "Analyse expiree, relance le scout." : statusData.error || "Erreur statut backend");
        if (statusData.status === "error") throw new Error(statusData.error || "Erreur pendant l'analyse");

        status = statusData;
        updateProgress(statusData.progress);
        setLoadingInfo({
          message: statusData.message || "Analyse en cours",
          estimatedLabel: statusData.estimatedLabel || startData.estimatedLabel || "",
          remainingLabel: statusData.remainingLabel || "",
          apiEnvironment: statusData.apiEnvironment || startData.apiEnvironment || "",
        });
      }

      let data = null;
      for (let attempt = 0; attempt < 30; attempt++) {
        const resultRes = await fetch(`${API_BASE_URL}/scout/result/${startData.jobId}`);
        const resultData = await resultRes.json().catch(() => ({}));

        if (resultRes.status === 202 || resultData.error === "RESULT_NOT_READY") {
          setLoadingInfo(prev => ({
            ...prev,
            message: "Finalisation du resultat",
            remainingLabel: "quelques secondes",
          }));
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }

        if (!resultRes.ok) throw new Error(resultData.error || "Erreur resultat backend");

        if (Array.isArray(resultData.team1) && Array.isArray(resultData.team2)) {
          data = resultData;
          break;
        }

        setLoadingInfo(prev => ({
          ...prev,
          message: "Verification du resultat",
          remainingLabel: "quelques secondes",
        }));
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      if (!data) throw new Error("Resultat scout incomplet, relance l'analyse.");

      updateProgress(100);
      setLoadingInfo(prev => ({ ...prev, message: "Analyse terminee", remainingLabel: "0s" }));

      const sortTeamByRoles = (teamData, rolesArr) => {
        if (!teamData || !rolesArr) return teamData;
        const paired = teamData.map((p, i) => ({ player: p, role: rolesArr[i] || "NONE" }));
        const sorted = [];
        ROLES_ORDER.forEach(targetRole => {
          const found = paired.find(p => p.role === targetRole);
          if (found) sorted.push(found.player);
        });
        paired.forEach(p => {
          if (!ROLES_ORDER.includes(p.role) || !sorted.includes(p.player)) sorted.push(p.player);
        });
        return sorted.map((player, index) => ({ ...player, role: ROLES_ORDER[index % ROLES_ORDER.length] }));
      };

      setAllyPlayers(sortTeamByRoles(data.team1 || [], teamRoles.team1));
      setEnemyPlayers(sortTeamByRoles(data.team2 || [], teamRoles.team2));
      
      // Petit délai pour laisser l'utilisateur voir le 100% avant d'afficher la page
      setTimeout(() => setHasResult(true), 500);

    } catch (err) { 
      setError(err.message || "Erreur inconnue"); 
      setProgress(prev => prev > 0 ? prev : 0);
      setLoadingInfo({ message: "", estimatedLabel: "", remainingLabel: "", apiEnvironment: "" });
    } finally { 
      setLoading(false); 
    }
  };

  const handleReliabilityScout = () => {};

  const handleReset = () => {
    try { window.sessionStorage.removeItem(LAST_SCOUT_STORAGE_KEY); } catch (_) {}
    setSelectedPlayer(null); setAllyPlayers([]); setEnemyPlayers([]);
    setSelectedChamps({ ally: {}, enemy: {} }); setValidatedTeams(null);
    setError(""); setHasResult(false); setTeam1Url(""); setTeam2Url(""); setProgress(0);
    setLoadingInfo({ message: "", estimatedLabel: "", remainingLabel: "", apiEnvironment: "" });
      setShareUrl(""); setShareExpiresAt(null);
      setExperimentalLabReturn(null);
      setReliabilityMatch(null); setMiddleView("impact");
  };

  const handleShare = async () => {
    try {
      setSharing(true);
      setError("");

      const res = await fetch(`${API_BASE_URL}/scout/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          result: {
            team1: allyPlayers,
            team2: enemyPlayers,
            selectedChamps,
            activeCategories,
            reliabilityMatch,
          },
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Impossible de creer le lien partage");

      const url = `${window.location.origin}/scout/shared/${data.shareId}`;
      setShareUrl(url);
      setShareExpiresAt(data.expiresAt || null);

      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
        } catch (_) {}
      }
    } catch (err) {
      setError(err.message || "Erreur lors du partage");
    } finally {
      setSharing(false);
    }
  };

  const toggleCategory = (cat) => setActiveCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  const handleChampionSelect = (side, playerIndex, champName) => {
    setSelectedChamps(prev => {
      const newSideChamps = { ...prev[side] };
      if (newSideChamps[playerIndex] === champName) delete newSideChamps[playerIndex];
      else newSideChamps[playerIndex] = champName;
      return { ...prev, [side]: newSideChamps };
    });
  };

  const applyRoleOrderToTeam = (team) => team.map((player, index) => ({
    ...player,
    role: ROLES_ORDER[index % ROLES_ORDER.length],
  }));

  const handlePlayerCardDrop = (side, targetIndex) => {
    if (!draggedScoutCard || draggedScoutCard.side !== side) {
      setDraggedScoutCard(null);
      return;
    }

    const sourceIndex = draggedScoutCard.index;
    if (sourceIndex === targetIndex) {
      setDraggedScoutCard(null);
      return;
    }

    const updateTeam = side === "ally" ? setAllyPlayers : setEnemyPlayers;
    updateTeam((previousTeam) => {
      const nextTeam = [...previousTeam];
      const [movedPlayer] = nextTeam.splice(sourceIndex, 1);
      nextTeam.splice(targetIndex, 0, movedPlayer);
      return applyRoleOrderToTeam(nextTeam);
    });

    setSelectedChamps((previous) => ({ ...previous, [side]: {} }));
    setSelectedPlayer(null);
    suppressNextCardClick.current = true;
    window.setTimeout(() => { suppressNextCardClick.current = false; }, 160);
    setDraggedScoutCard(null);
  };

  const renderScoutPlayerCard = (side, player, index) => {
    const isAlly = side === "ally";
    const absoluteIndex = isAlly ? index : index + allyPlayers.length;
    const isDragging = draggedScoutCard?.side === side && draggedScoutCard?.index === index;
    const isSameTeamDrag = draggedScoutCard?.side === side;
    const accent = isAlly ? C.team1 : C.team2;

    return (
      <div
        key={`${side}-${getPlayerName(player)}-${index}`}
        draggable
        title="Drag this card to change the role order"
        onDragStart={(event) => {
          setDraggedScoutCard({ side, index });
          event.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          handlePlayerCardDrop(side, index);
        }}
        onDragEnd={() => {
          setDraggedScoutCard(null);
        }}
        style={{
          minWidth: 0,
          borderRadius: "16px",
          border: isSameTeamDrag ? `1px dashed ${accent}` : "1px solid transparent",
          padding: "2px",
          opacity: isDragging ? 0.45 : 1,
          cursor: "grab",
          transition: "opacity 0.16s ease, border-color 0.16s ease, background 0.16s ease",
          background: isSameTeamDrag && !isDragging ? `${accent}0D` : "transparent",
        }}
      >
        <PlayerScoutCard
          player={{ ...player, role: normalizeDraftRole(player?.role) || ROLES_ORDER[index % ROLES_ORDER.length] }}
          side={side}
          roleLabel={ROLES_ORDER[index % ROLES_ORDER.length]}
          onClick={() => {
            if (suppressNextCardClick.current) {
              suppressNextCardClick.current = false;
              return;
            }
            setSelectedPlayer(absoluteIndex);
          }}
          selectedChampion={selectedChamps[side][index]}
          onChampionSelect={(champ) => handleChampionSelect(side, index, champ)}
          ddVersion={ddVersion}
        />
      </div>
    );
  };

  const aggregateTeamImpact = (team, side) => {
    const result = {};
    team.forEach((player, index) => {
      let impactToUse = player.impact || {};
      const selectedChampName = selectedChamps[side][index];
      if (selectedChampName && player.champions) {
        const champData = player.champions.find(c => c.champion === selectedChampName);
        if (champData && champData.impact) impactToUse = champData.impact;
      }
      Object.keys(impactToUse).forEach(key => {
        if (!result[key]) result[key] = 0;
        result[key] += impactToUse[key];
      });
    });
    return result;
  };

  const team1Impact = aggregateTeamImpact(allyPlayers, "ally");
  const team2Impact = aggregateTeamImpact(enemyPlayers, "enemy");

  const activeStats = Object.entries(CATEGORIZED_STATS)
    .filter(([cat]) => activeCategories.includes(cat))
    .flatMap(([, stats]) => stats)
    .filter(stat => Object.keys(team1Impact).includes(stat) || Object.keys(team2Impact).includes(stat));

  const chartData1 = activeStats.map(stat => ({ stat, value: team1Impact[stat] || 0 }));
  const chartData2 = activeStats.map(stat => ({ stat, value: team2Impact[stat] || 0 }));
  const chartDataDiff = activeStats.map(stat => ({ stat, value: (team1Impact[stat] || 0) - (team2Impact[stat] || 0) }));

  const team1Total = activeStats.reduce((s, stat) => s + (team1Impact[stat] || 0), 0);
  const team2Total = activeStats.reduce((s, stat) => s + (team2Impact[stat] || 0), 0);
  const impactDiff = team1Total - team2Total;
  const formatDuration = (seconds) => {
    const total = Number(seconds) || 0;
    const min = Math.floor(total / 60);
    const sec = String(total % 60).padStart(2, "0");
    return `${min}:${sec}`;
  };
  const formatGold = (gold) => `${((Number(gold) || 0) / 1000).toFixed(1)}k`;
  const formatNumber = (value) => Number(value || 0).toLocaleString("fr-FR");
  const getReliabilityTeamName = (teamId) => {
    if (!reliabilityMatch) return `Equipe ${teamId}`;
    return Number(teamId) === Number(reliabilityMatch.searchedTeamId) ? "Equipe du joueur" : "Equipe adverse";
  };
  const getReliabilityTeamTotals = (teamId) => {
    const players = (reliabilityMatch?.participants || []).filter(p => Number(p.teamId) === Number(teamId));
    return players.reduce((acc, p) => ({
      kills: acc.kills + Number(p.kills || 0),
      deaths: acc.deaths + Number(p.deaths || 0),
      assists: acc.assists + Number(p.assists || 0),
      gold: acc.gold + Number(p.gold || 0),
      cs: acc.cs + Number(p.cs_total || 0),
      damage: acc.damage + Number(p.damageToChampions || 0),
      taken: acc.taken + Number(p.damageTaken || 0),
      vision: acc.vision + Number(p.visionScore || 0),
    }), { kills: 0, deaths: 0, assists: 0, gold: 0, cs: 0, damage: 0, taken: 0, vision: 0 });
  };

  const allPlayersValid = validatedTeams && 
    validatedTeams.team1.every(p => p.exists) && 
    validatedTeams.team2.every(p => p.exists) &&
    validatedTeams.team1.length === 5 && 
    validatedTeams.team2.length === 5;

  const shareExpirationLabel = shareExpiresAt
    ? new Date(shareExpiresAt * 1000).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    : null;

  /* ── Couleurs du système de design (alignées sur les CSS variables) ── */
  const C = {
    bg:        "#09090b",
    surface:   "#18181b",
    surface2:  "#27272a",
    border:    "#27272a",
    borderSub: "#3f3f46",
    muted:     "#71717a",
    accent:    "#3b82f6",
    accentH:   "#60a5fa",
    team1:     "#4ade80",  /* vert — équipe 1 */
    team2:     "#f87171",  /* rouge — équipe 2 */
    diff:      "#93c5fd",  /* bleu clair — différentiel */
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, padding: "20px 24px 32px", color: "#e4e4e7", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <h1 style={{ marginBottom: "20px", fontSize: "24px", fontWeight: "800", color: "#f4f4f5" }}>Draft Scout</h1>

      {shareLoading && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: "12px", padding: "18px", marginBottom: "24px", maxWidth: "720px" }}>
          <div style={{ fontSize: "14px", fontWeight: "700", color: "#f4f4f5", marginBottom: "8px" }}>Chargement de l'analyse partagee</div>
          <div style={{ height: "6px", background: C.surface2, borderRadius: "999px", overflow: "hidden" }}>
            <div style={{ width: "55%", height: "100%", background: C.accent, borderRadius: "999px" }} />
          </div>
        </div>
      )}

      {!hasResult && !isSharedView && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: "12px", padding: "18px", marginBottom: "24px", display: "flex", flexDirection: "column", gap: "12px", maxWidth: "1200px" }}>

          <input type="text" value={team1Url} onChange={(e) => {setTeam1Url(e.target.value); setValidatedTeams(null);}} placeholder="Lien OP.GG multisearch équipe 1 (5 joueurs max)" style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.bg, color: "#e4e4e7", outline: "none", fontSize: "14px" }} />
          <input type="text" value={team2Url} onChange={(e) => {setTeam2Url(e.target.value); setValidatedTeams(null);}} placeholder="Lien OP.GG multisearch équipe 2 (5 joueurs max)" style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.bg, color: "#e4e4e7", outline: "none", fontSize: "14px" }} />

          {!validatedTeams && (
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={handleValidate} disabled={isValidating || !team1Url.trim() || !team2Url.trim()} style={{ padding: "10px 16px", borderRadius: "8px", border: "none", background: C.accent, color: "white", fontWeight: "700", fontSize: "14px", cursor: isValidating ? "default" : "pointer", opacity: isValidating || !team1Url.trim() || !team2Url.trim() ? 0.5 : 1 }}>
                {isValidating ? "Vérification en cours..." : "1. Vérifier les équipes"}
              </button>
              <button onClick={() => { setAllyPlayers(mockTeam1); setEnemyPlayers(mockTeam2); setSelectedChamps({ ally: {}, enemy: {} }); setExperimentalLabReturn(null); setHasResult(true); }} style={{ padding: "10px 16px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: "transparent", color: C.muted, fontWeight: "600", fontSize: "14px", cursor: "pointer" }}>Explore with fake data</button>
            </div>
          )}

          {!validatedTeams && reliabilityOpen && (
            <div style={{ marginTop: "4px", padding: "14px", background: C.bg, border: "1px solid rgba(74,222,128,0.22)", borderRadius: "10px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", alignItems: "center" }}>
              <input type="text" value={reliabilityName} onChange={(e) => setReliabilityName(e.target.value)} placeholder="Nom d'invocateur" style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.surface, color: "#e4e4e7", outline: "none", fontSize: "14px" }} />
              <input type="text" value={reliabilityTag} onChange={(e) => setReliabilityTag(e.target.value)} placeholder="Tag" style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.surface, color: "#e4e4e7", outline: "none", fontSize: "14px" }} />
              <button onClick={handleReliabilityScout} disabled={loading || !reliabilityName.trim() || !reliabilityTag.trim()} style={{ position: "relative", overflow: "hidden", padding: "10px 16px", borderRadius: "8px", border: "none", background: C.surface2, color: "#f4f4f5", fontWeight: "800", fontSize: "13px", cursor: loading ? "default" : "pointer", opacity: loading || !reliabilityName.trim() || !reliabilityTag.trim() ? 0.55 : 1, minWidth: 0 }}>
                <div style={{ position: "absolute", top: 0, left: 0, height: "100%", width: `${progress}%`, background: C.team1, transition: "width 0.4s ease-out", zIndex: 0 }} />
                <span style={{ position: "relative", zIndex: 1 }}>{loading ? `Test en cours... ${Math.round(progress)}%` : "Lancer le test"}</span>
              </button>
              {loading && (
                <div style={{ gridColumn: "1 / -1", color: C.muted, fontSize: "12px", textAlign: "center" }}>
                  {loadingInfo.message || "Analyse en cours"} · estime {loadingInfo.estimatedLabel || "calcul..."} · restant {loadingInfo.remainingLabel || "calcul..."}
                </div>
              )}
            </div>
          )}

          {validatedTeams && (
            <div style={{ marginTop: "10px", padding: "16px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: "10px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <h3 style={{ fontSize: "14px", color: C.muted, margin: 0, fontWeight: "600" }}>Vérification et Assignation des Rôles</h3>
                <span style={{ fontSize: "12px", color: C.muted }}>Ordre final : Top · Jungle · Mid · Adc · Support</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr", gap: "24px", marginBottom: "20px" }}>
                {/* ÉQUIPE 1 */}
                <div>
                  <h4 style={{ color: C.accentH, marginBottom: "8px", fontSize: "13px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.05em" }}>Équipe 1 ({validatedTeams.team1.length}/5)</h4>
                  {validatedTeams.team1.map((p, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                      {p.exists ? <span style={{ color: C.team1 }}>✔</span> : <span style={{ color: C.team2 }}>✖</span>}
                      <span style={{ color: p.exists ? "#f4f4f5" : C.team2, textDecoration: p.exists ? "none" : "line-through", fontSize: "14px", fontWeight: "600" }}>{p.gameName}</span>
                      {p.exists ? (
                        <select value={teamRoles.team1[i] || ""} onChange={(e) => handleRoleChange("team1", i, e.target.value)} style={{ background: "rgba(59,130,246,0.12)", color: C.accentH, border: `1px solid rgba(59,130,246,0.3)`, borderRadius: "6px", padding: "4px 8px", fontSize: "12px", fontWeight: "bold", marginLeft: "auto", outline: "none", cursor: "pointer" }}>
                          {ROLES_ORDER.map(r => <option key={r} value={r} style={{ background: C.surface, color: "#f4f4f5" }}>{r}</option>)}
                        </select>
                      ) : <span title={p.error || getPlayerName(p)} style={{ fontSize: "11px", color: C.team2, marginLeft: "auto", fontWeight: "bold", textAlign: "right", maxWidth: "190px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.error ? `Erreur API : ${getPlayerName(p)}` : `Joueur introuvable : ${getPlayerName(p)}`}</span>}
                    </div>
                  ))}
                </div>

                {/* ÉQUIPE 2 */}
                <div>
                  <h4 style={{ color: C.team2, marginBottom: "8px", fontSize: "13px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.05em" }}>Équipe 2 ({validatedTeams.team2.length}/5)</h4>
                  {validatedTeams.team2.map((p, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                      {p.exists ? <span style={{ color: C.team1 }}>✔</span> : <span style={{ color: C.team2 }}>✖</span>}
                      <span style={{ color: p.exists ? "#f4f4f5" : C.team2, textDecoration: p.exists ? "none" : "line-through", fontSize: "14px", fontWeight: "600" }}>{p.gameName}</span>
                      {p.exists ? (
                        <select value={teamRoles.team2[i] || ""} onChange={(e) => handleRoleChange("team2", i, e.target.value)} style={{ background: "rgba(248,113,113,0.12)", color: "#fca5a5", border: `1px solid rgba(248,113,113,0.3)`, borderRadius: "6px", padding: "4px 8px", fontSize: "12px", fontWeight: "bold", marginLeft: "auto", outline: "none", cursor: "pointer" }}>
                          {ROLES_ORDER.map(r => <option key={r} value={r} style={{ background: C.surface, color: "#f4f4f5" }}>{r}</option>)}
                        </select>
                      ) : <span title={p.error || getPlayerName(p)} style={{ fontSize: "11px", color: C.team2, marginLeft: "auto", fontWeight: "bold", textAlign: "right", maxWidth: "190px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.error ? `Erreur API : ${getPlayerName(p)}` : `Joueur introuvable : ${getPlayerName(p)}`}</span>}
                    </div>
                  ))}
                </div>

                {/* EXPLICATIONS */}
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "16px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <h4 style={{ color: "#f4f4f5", marginBottom: "12px", fontSize: "14px", fontWeight: "700" }}>Comment utiliser le Scout ?</h4>
                  <ul style={{ color: C.muted, fontSize: "13px", lineHeight: "1.7", paddingLeft: "16px", margin: 0 }}>
                    <li style={{ marginBottom: "8px" }}><strong style={{ color: "#e4e4e7" }}>Étape 1 :</strong> Vérifiez que tous les joueurs ont été trouvés par l'API Riot (✔).</li>
                    <li style={{ marginBottom: "8px" }}><strong style={{ color: "#e4e4e7" }}>Étape 2 :</strong> Ajustez les rôles pour aligner les postes face à face.</li>
                    <li style={{ marginBottom: "8px" }}><strong style={{ color: "#e4e4e7" }}>Étape 3 :</strong> Lancez l'analyse. L'outil calcule l'impact de chaque joueur.</li>
                    <li><strong style={{ color: "#e4e4e7" }}>Objectif :</strong> Cliquez sur les champions pour simuler l'impact d'une draft.</li>
                  </ul>
                </div>
              </div>

              {/* BOUTON D'ANALYSE ET PROGRESS BAR */}
              <div style={{ marginTop: "20px" }}>
                <button
                  onClick={handleScout}
                  disabled={loading || !allPlayersValid}
                  style={{ position: "relative", overflow: "hidden", padding: "12px 18px", borderRadius: "8px", border: "none", background: C.surface2, color: "#f4f4f5", fontWeight: "700", fontSize: "14px", cursor: (loading || !allPlayersValid) ? "default" : "pointer", opacity: (!allPlayersValid) ? 0.45 : 1, width: "100%", zIndex: 1 }}
                >
                  <div style={{ position: "absolute", top: 0, left: 0, height: "100%", width: `${progress}%`, background: C.accent, transition: "width 0.4s ease-out", zIndex: -1 }}></div>
                  <span style={{ position: "relative", zIndex: 2 }}>
                    {loading ? `Analyse en cours... (${Math.round(progress)}%)` : "2. Lancer l'analyse experte"}
                  </span>
                </button>

                {!allPlayersValid && (
                  <p style={{ color: C.team2, fontSize: "12px", marginTop: "8px", textAlign: "center" }}>
                    Corrigez les liens OP.GG. Les deux équipes doivent avoir exactement 5 joueurs existants.
                  </p>
                )}
                {loading && (
                  <div style={{ marginTop: "10px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "8px" }}>
                    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: "8px", padding: "10px 12px" }}>
                      <div style={{ color: C.muted, fontSize: "10px", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>Environnement</div>
                      <div style={{ color: "#f4f4f5", fontSize: "13px", fontWeight: "700" }}>{loadingInfo.apiEnvironment === "prod" ? "Prod" : "Local / cle dev"}</div>
                    </div>
                    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: "8px", padding: "10px 12px" }}>
                      <div style={{ color: C.muted, fontSize: "10px", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>Temps estime</div>
                      <div style={{ color: "#f4f4f5", fontSize: "13px", fontWeight: "700" }}>{loadingInfo.estimatedLabel || "Calcul..."}</div>
                    </div>
                    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: "8px", padding: "10px 12px" }}>
                      <div style={{ color: C.muted, fontSize: "10px", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>Restant</div>
                      <div style={{ color: "#f4f4f5", fontSize: "13px", fontWeight: "700" }}>{loadingInfo.remainingLabel || "Calcul..."}</div>
                    </div>
                    <div style={{ gridColumn: "1 / -1", color: C.muted, fontSize: "12px", textAlign: "center", fontStyle: "italic" }}>
                      {loadingInfo.message || "L'API Riot limite parfois la vitesse. La progression suit le backend en temps reel."}
                    </div>
                    <a
                      href={GUESS_WHO_WON_URL}
                      target="_blank"
                      rel="noreferrer"
                      style={{ gridColumn: "1 / -1", justifySelf: "center", display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: "38px", padding: "0 16px", borderRadius: "8px", border: "1px solid rgba(96,165,250,0.45)", background: "rgba(59,130,246,0.12)", color: C.accentH, fontSize: "13px", fontWeight: "800", textDecoration: "none" }}
                    >
                      Play Guess Who Won while it loads
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {hasResult && (
        <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <Link to="/" style={{ padding: "10px 16px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.surface, color: "#f4f4f5", fontWeight: "700", fontSize: "14px", textDecoration: "none" }}>
            Home
          </Link>
          {!isSharedView && (
            <>
              <button onClick={handleReset} style={{ padding: "10px 16px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: "transparent", color: C.muted, fontWeight: "600", fontSize: "14px", cursor: "pointer" }}>Nouvelle analyse</button>
              <button onClick={handleShare} disabled={sharing} style={{ padding: "10px 16px", borderRadius: "8px", border: "none", background: C.accent, color: "white", fontWeight: "700", fontSize: "14px", cursor: sharing ? "default" : "pointer", opacity: sharing ? 0.65 : 1 }}>
                {sharing ? "Creation du lien..." : "Partager l'analyse"}
              </button>
              {experimentalLabReturn && (
                <Link
                  to="/experimental"
                  state={{ labReturn: experimentalLabReturn }}
                  style={{ padding: "10px 16px", borderRadius: "8px", border: "1px solid rgba(96,165,250,0.45)", background: "rgba(59,130,246,0.10)", color: C.accentH, fontWeight: "700", fontSize: "14px", textDecoration: "none" }}
                >
                  Retour au labo
                </Link>
              )}
            </>
          )}
          {isSharedView && (
            <div style={{ padding: "10px 14px", borderRadius: "8px", border: `1px solid ${C.borderSub}`, background: C.surface, color: C.muted, fontSize: "13px", fontWeight: "600" }}>
              Analyse partagee{shareExpirationLabel ? ` · expire a ${shareExpirationLabel}` : ""}
            </div>
          )}
          {shareUrl && (
            <div style={{ padding: "10px 14px", borderRadius: "8px", border: "1px solid rgba(74,222,128,0.35)", background: "rgba(74,222,128,0.08)", color: C.team1, fontSize: "13px", fontWeight: "700", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              Lien copie · expire a {shareExpirationLabel || "dans 1h"} · {shareUrl}
            </div>
          )}
        </div>
      )}

      {error && <div style={{ marginBottom: "18px", color: "#fca5a5", background: "#1c0a0a", border: "1px solid #5a1f1f", padding: "12px 14px", borderRadius: "10px", fontSize: "14px" }}>{error}</div>}

      {hasResult && (
        <>
          <div style={{ marginBottom: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
              <div style={{ width: "3px", height: "18px", borderRadius: "2px", background: C.team1, flexShrink: 0 }} />
              <h2 style={{ margin: 0, color: C.team1, fontSize: "13px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.07em" }}>Équipe 1</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(220px, 1fr))", gap: "16px" }}>
              {allyPlayers.map((player, index) => renderScoutPlayerCard("ally", player, index))}
            </div>
          </div>

          <div style={{ margin: "36px 0", background: C.surface, border: `1px solid ${C.border}`, borderRadius: "14px", padding: "24px" }}>
            {/* ── Header : titre + toggle + filtres ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "14px", flexWrap: "wrap", marginBottom: showCharts ? "20px" : "0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#f4f4f5" }}>Scout workspace</h2>
                <div style={{ display: "flex", gap: "4px", padding: "3px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: "8px", flexWrap: "wrap" }}>
                  {[
                    ["impact", "Impact"],
                    ["draft", "Draft Plan"],
                    ...(reliabilityMatch ? [["truth", "Issue de la game"]] : []),
                  ].map(([id, label]) => (
                    <button key={id} onClick={() => setMiddleView(id)} style={{ padding: "5px 10px", borderRadius: "6px", border: "none", background: middleView === id ? C.accent : "transparent", color: middleView === id ? "white" : C.muted, fontSize: "12px", fontWeight: "800", cursor: "pointer" }}>
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowCharts(p => !p)}
                  style={{ padding: "4px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", cursor: "pointer", background: "transparent", border: `1px solid ${C.borderSub}`, color: C.muted, transition: "all 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accentH; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.borderSub; e.currentTarget.style.color = C.muted; }}
                >
                  {showCharts ? "▲ Réduire" : "▼ Afficher"}
                </button>
              </div>
              {showCharts && middleView === "impact" && (
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {Object.keys(CATEGORIZED_STATS).map(cat => {
                    const isActive = activeCategories.includes(cat);
                    return (
                      <button key={cat} onClick={() => toggleCategory(cat)} style={{ padding: "5px 11px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", cursor: "pointer", transition: "all 0.15s", background: isActive ? "rgba(59,130,246,0.15)" : "transparent", border: isActive ? `1px solid ${C.accent}` : `1px solid ${C.borderSub}`, color: isActive ? C.accentH : C.muted }}>
                        {cat}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Panels visualisation ── */}
            {showCharts && middleView === "impact" && (
              <>
              {/* Bandeau totaux */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "12px", alignItems: "center", marginBottom: "14px" }}>
                {/* Total Équipe 1 */}
                <div style={{ background: "linear-gradient(135deg, rgba(74,222,128,0.08) 0%, rgba(9,9,11,0) 100%)", border: "1px solid rgba(74,222,128,0.3)", borderRadius: "10px", padding: "12px 20px", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <span style={{ fontSize: "10px", fontWeight: "700", color: C.team1, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>Total Équipe 1</span>
                  <span style={{ fontSize: "26px", fontWeight: "800", color: team1Total >= team2Total ? C.team1 : "#f4f4f5" }}>
                    {team1Total > 0 ? "+" : ""}{team1Total.toFixed(1)}%
                  </span>
                </div>

                {/* Différence */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", minWidth: "110px" }}>
                  <span style={{ fontSize: "10px", fontWeight: "700", color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>Écart</span>
                  <span style={{ fontSize: "22px", fontWeight: "800", color: impactDiff > 0 ? C.team1 : impactDiff < 0 ? C.team2 : C.muted }}>
                    {impactDiff > 0 ? "+" : ""}{impactDiff.toFixed(1)}%
                  </span>
                  <span style={{ fontSize: "10px", color: C.muted }}>Éq1 − Éq2</span>
                </div>

                {/* Total Équipe 2 */}
                <div style={{ background: "linear-gradient(135deg, rgba(248,113,113,0.08) 0%, rgba(9,9,11,0) 100%)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: "10px", padding: "12px 20px", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <span style={{ fontSize: "10px", fontWeight: "700", color: C.team2, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>Total Équipe 2</span>
                  <span style={{ fontSize: "26px", fontWeight: "800", color: team2Total > team1Total ? C.team2 : "#f4f4f5" }}>
                    {team2Total > 0 ? "+" : ""}{team2Total.toFixed(1)}%
                  </span>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", height: "450px" }}>
                {/* Équipe 1 — bordure verte */}
                <div style={{ background: "linear-gradient(180deg, #0d1a10 0%, #09090b 100%)", border: "1px solid rgba(74,222,128,0.35)", borderTop: "2px solid rgba(74,222,128,0.7)", borderRadius: "10px", padding: "16px", display: "flex", flexDirection: "column" }}>
                  <h3 style={{ fontSize: "13px", color: C.team1, marginBottom: "16px", textAlign: "center", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Équipe 1</h3>
                  <div style={{ flex: 1, position: "relative" }}>
                    {chartData1.length > 0 ? <ChartImpact data={chartData1} /> : <div style={{ textAlign: "center", color: C.muted, marginTop: "50%" }}>Sélectionnez une catégorie</div>}
                  </div>
                </div>

                {/* Différence — bordure bleue */}
                <div style={{ background: "linear-gradient(180deg, #0d1020 0%, #09090b 100%)", border: "1px solid rgba(59,130,246,0.35)", borderTop: "2px solid rgba(59,130,246,0.7)", borderRadius: "10px", padding: "16px", display: "flex", flexDirection: "column" }}>
                  <h3 style={{ fontSize: "13px", color: C.diff, marginBottom: "16px", textAlign: "center", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.05em" }}>Différence (Eq1 − Eq2)</h3>
                  <div style={{ flex: 1, position: "relative" }}>
                    {chartDataDiff.length > 0 ? <ChartImpact data={chartDataDiff} /> : <div style={{ textAlign: "center", color: C.muted, marginTop: "50%" }}>Sélectionnez une catégorie</div>}
                  </div>
                </div>

                {/* Équipe 2 — bordure rouge */}
                <div style={{ background: "linear-gradient(180deg, #1a0d0d 0%, #09090b 100%)", border: "1px solid rgba(248,113,113,0.35)", borderTop: "2px solid rgba(248,113,113,0.7)", borderRadius: "10px", padding: "16px", display: "flex", flexDirection: "column" }}>
                  <h3 style={{ fontSize: "13px", color: C.team2, marginBottom: "16px", textAlign: "center", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Équipe 2</h3>
                  <div style={{ flex: 1, position: "relative" }}>
                    {chartData2.length > 0 ? <ChartImpact data={chartData2} /> : <div style={{ textAlign: "center", color: C.muted, marginTop: "50%" }}>Sélectionnez une catégorie</div>}
                  </div>
                </div>
              </div>
              </>
            )}

            <div style={{ display: showCharts && middleView === "draft" ? "block" : "none" }}>
              <DraftPlanPanel
                allyPlayers={allyPlayers}
                enemyPlayers={enemyPlayers}
                ddVersion={ddVersion}
                theme={C}
              />
            </div>

            {showCharts && middleView === "truth" && reliabilityMatch && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
                <div style={{ background: reliabilityMatch.searchedWin ? "linear-gradient(180deg, #0d1a10 0%, #09090b 100%)" : "linear-gradient(180deg, #1a0d0d 0%, #09090b 100%)", border: reliabilityMatch.searchedWin ? "1px solid rgba(74,222,128,0.35)" : "1px solid rgba(248,113,113,0.35)", borderRadius: "12px", padding: "18px" }}>
                  <div style={{ fontSize: "11px", fontWeight: "800", color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "8px" }}>Game cible</div>
                  <div style={{ fontSize: "24px", fontWeight: "900", color: reliabilityMatch.searchedWin ? C.team1 : C.team2, marginBottom: "6px" }}>
                    {reliabilityMatch.searchedWin ? "Victoire" : "Defaite"}
                  </div>
                  <div style={{ color: "#f4f4f5", fontSize: "14px", fontWeight: "800", marginBottom: "4px" }}>
                    {reliabilityMatch.searchedPlayer?.gameName}#{reliabilityMatch.searchedPlayer?.tagLine}
                  </div>
                  <div style={{ color: C.muted, fontSize: "12px", marginBottom: "14px" }}>
                    {reliabilityMatch.searchedChampion} · {formatDuration(reliabilityMatch.gameDuration)}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px" }}>
                    {(reliabilityMatch.teams || []).map((team) => {
                      const totals = getReliabilityTeamTotals(team.teamId);
                      return (
                        <div key={team.teamId} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "10px" }}>
                          <div style={{ fontSize: "10px", color: team.win ? C.team1 : C.team2, fontWeight: "900", textTransform: "uppercase", marginBottom: "6px" }}>
                            {getReliabilityTeamName(team.teamId)} · {team.win ? "Win" : "Lose"}
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", color: C.muted, fontSize: "11px", marginBottom: "8px" }}>
                            <span>KDA {totals.kills}/{totals.deaths}/{totals.assists}</span><span>Gold {formatGold(totals.gold)}</span>
                            <span>Degats {formatNumber(totals.damage)}</span><span>Vision {totals.vision}</span>
                            <span>CS {totals.cs}</span><span>Tank {formatNumber(totals.taken)}</span>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", color: C.muted, fontSize: "11px", paddingTop: "8px", borderTop: `1px solid ${C.border}` }}>
                            <span>Tours {team.towers}</span><span>Drakes {team.dragons}</span>
                            <span>Barons {team.barons}</span><span>Inhibs {team.inhibitors}</span>
                            <span>Herald {team.heralds}</span><span>Grubs {team.grubs}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: "12px", padding: "14px", minWidth: 0 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "12px" }}>
                    {[...new Set((reliabilityMatch.participants || []).map(p => p.teamId))].map((teamId) => {
                      const teamPlayers = (reliabilityMatch.participants || []).filter(p => p.teamId === teamId);
                      const teamWon = teamPlayers.some(p => p.win);
                      return (
                        <div key={teamId} style={{ minWidth: 0 }}>
                          <div style={{ fontSize: "11px", fontWeight: "900", color: teamWon ? C.team1 : C.team2, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "8px" }}>
                            {getReliabilityTeamName(teamId)} · {teamWon ? "Win" : "Lose"}
                          </div>
                          <div style={{ display: "grid", gap: "7px" }}>
                            {teamPlayers.map((p) => (
                              <div key={`${p.teamId}-${p.gameName}-${p.champion}`} style={{ display: "grid", gridTemplateColumns: "34px minmax(0,1fr) auto", gap: "8px", alignItems: "center", padding: "7px 8px", borderRadius: "9px", background: C.surface, border: `1px solid ${C.border}` }}>
                                <img src={`https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/champion/${p.champion}.png`} alt={p.champion} style={{ width: "34px", height: "34px", borderRadius: "7px", objectFit: "cover" }} />
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: "12px", color: "#f4f4f5", fontWeight: "900", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.champion}</div>
                                  <div style={{ fontSize: "10px", color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.gameName} · {p.role}</div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                  <div style={{ fontSize: "11px", color: "#f4f4f5", fontWeight: "800" }}>{p.kills}/{p.deaths}/{p.assists}</div>
                                  <div style={{ fontSize: "10px", color: C.muted }}>{p.cs_total} CS · {formatGold(p.gold)} · {formatNumber(p.damageToChampions)} dmg</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
              <div style={{ width: "3px", height: "18px", borderRadius: "2px", background: C.team2, flexShrink: 0 }} />
              <h2 style={{ margin: 0, color: C.team2, fontSize: "13px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.07em" }}>Équipe 2</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(220px, 1fr))", gap: "16px" }}>
              {enemyPlayers.map((player, index) => renderScoutPlayerCard("enemy", player, index))}
            </div>
          </div>
        </>
      )}

      {selectedPlayer !== null && players[selectedPlayer] && (
        <ScoutDetailModal
          player={players[selectedPlayer]}
          side={selectedPlayer < allyPlayers.length ? "ally" : "enemy"}
          onClose={() => setSelectedPlayer(null)}
          ddVersion={ddVersion}
        />
      )}
    </div>
  );
}
